import { useState, useRef, useEffect, useCallback, createContext, useContext } from "react";

const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Bangers&family=Nunito:wght@400;600;700;800&display=swap');`;

const P = {
  bg:"#0D1B2A", card:"#1A2E42", yellow:"#FFD166", mint:"#06D6A0",
  coral:"#FF6B6B", sky:"#74C7EC", lavender:"#B8A9FF", peach:"#FFB347",
  white:"#F8F9FA", muted:"#8BAFC8", black:"#0D1B2A",
};

const CSS = `
  *{box-sizing:border-box;margin:0;padding:0;}
  html,body{background:${P.bg};overflow-x:hidden;}
  button{cursor:pointer;border:none;background:none;}
  button:focus-visible{outline:2px solid ${P.sky};outline-offset:2px;}
  @media(prefers-reduced-motion:reduce){*{animation:none!important;transition:none!important;}}

  @keyframes dance{
    0%,100%{transform:translate(-50%,-50%) rotate(0deg) scale(1);}
    20%{transform:translate(-50%,-68%) rotate(-22deg) scale(1.28);}
    40%{transform:translate(-50%,-72%) rotate(22deg) scale(1.28);}
    60%{transform:translate(-50%,-62%) rotate(-14deg) scale(1.14);}
    80%{transform:translate(-50%,-58%) rotate(14deg) scale(1.14);}
  }
  @keyframes reveal{from{opacity:0;transform:translateY(22px);}to{opacity:1;transform:translateY(0);}}
  @keyframes float{0%,100%{transform:translateY(0);}50%{transform:translateY(-9px);}}
  @keyframes aurora{0%,100%{transform:skewX(0deg) scaleY(1);opacity:.7;}50%{transform:skewX(5deg) scaleY(1.07);opacity:.95;}}
  @keyframes aurora2{0%,100%{transform:skewX(0deg) scaleY(1);opacity:.5;}50%{transform:skewX(-4deg) scaleY(1.05);opacity:.8;}}
  @keyframes steam{0%{transform:translateY(0) scaleX(1);opacity:.5;}100%{transform:translateY(-70px) scaleX(2.2);opacity:0;}}
  @keyframes lava-pulse{0%,100%{opacity:.55;}50%{opacity:1;}}
  @keyframes geyser{0%,100%{transform:scaleY(.5) translateY(30px);opacity:.2;}45%,55%{transform:scaleY(1) translateY(0);opacity:.9;}}
  @keyframes shimmer{0%{background-position:-300% center;}100%{background-position:300% center;}}
  @keyframes badge-pop{0%{transform:scale(0) rotate(-10deg);opacity:0;}70%{transform:scale(1.1) rotate(3deg);opacity:1;}100%{transform:scale(1) rotate(0deg);opacity:1;}}

  @keyframes burst-up{0%{transform:translateY(0) scale(1);opacity:1;}100%{transform:translateY(-220px) scale(.5);opacity:0;}}
  @keyframes confetti-fall{0%{transform:translateY(-40px) rotate(0);opacity:1;}100%{transform:translateY(120vh) rotate(720deg);opacity:0;}}
  @keyframes bubble-rise{0%{transform:translateY(0) scale(1);opacity:.8;}100%{transform:translateY(-280px) scale(2);opacity:0;}}
  @keyframes bonhomme-sprint{
    0%  {transform:translateX(-180px) translateY(0)    rotate(0deg)   scale(1);}
    12% {transform:translateX(12vw)   translateY(-18px) rotate(-12deg) scale(1.1);}
    25% {transform:translateX(25vw)   translateY(0)    rotate(8deg)   scale(1);}
    37% {transform:translateX(37vw)   translateY(-22px) rotate(-15deg) scale(1.12);}
    50% {transform:translateX(50vw)   translateY(0)    rotate(10deg)  scale(1);}
    62% {transform:translateX(62vw)   translateY(-18px) rotate(-10deg) scale(1.1);}
    75% {transform:translateX(75vw)   translateY(0)    rotate(12deg)  scale(1);}
    88% {transform:translateX(88vw)   translateY(-20px) rotate(-8deg)  scale(1.08);}
    100%{transform:translateX(calc(100vw + 180px)) translateY(0) rotate(0deg) scale(1);}
  }
  @keyframes sparkle-pop{0%,100%{transform:scale(0);opacity:0;}50%{transform:scale(1.4);opacity:1;}}
`;

// ── LANGUAGE CONTEXT ─────────────────────────────────────────
const LangCtx = createContext('en');
const useLang = () => useContext(LangCtx);

const UI = {
  en: {
    cutTheCrap:"Cut the crap!", cutTheCrapTip:"I'm a busy person, I don't have the time to play games!",
    heroTagline:"Hi! I'm Gaëtan. Explore my career — one level at a time! 🇮🇸",
    heroSub:"AN INTERACTIVE CAREER ADVENTURE",
    heroDesc:"Senior IT Manager · 15+ years · 6 countries · Full-stack human 🧠",
    quickChallenge:"🎯 Quick Challenge",
    spotOn:"Spot on!", goodTryFull:"Good try — here's the full picture:", goodTryAgain:"Good try! Try again to unlock the full picture.",
    fullPicture:"✨ Full Picture", skillsUnlocked:"🛠 SKILLS UNLOCKED", toolsTech:"⚙️ TOOLS & TECH", funFact:"🎉 Fun Fact",
    playGame:"🔄 Play the game",
    questComplete:"Quest Complete!",
    questDesc:"You've unlocked the full profile of Gaëtan Goffe — a rare hybrid of deep technical knowledge and senior programme leadership, forged across 15+ years, 6 countries, and industries from banking to pharma to cloud infrastructure.",
    completeAll:"Complete All Levels", completeDesc:(n,t)=>`Answer the challenges in each level to reveal the full profile. ${n} / ${t} done.`,
    connectLinkedin:"CONNECT ON LINKEDIN →", unlockConnect:"UNLOCK TO CONNECT",
    footer:"Website developed in React.js with love (and Claude). Pictures generated from NanoBanana.",
    clickOrder:"Click the events in chronological order:",
    statement:(i,n)=>`STATEMENT ${i} / ${n}`,
    trueBtn:"TRUE", falseBtn:"FALSE", allDone:(s,t)=>`✅ All done! ${s}/${t} correct`,
    goBtn:"GO", checkBtn:"CHECK", typeAnswer:"Type your answer…", unscrambleIt:"Unscramble it…",
    hint:(h)=>`Hint: ${h}`, exactRight:"✅ Exactly right!", answerWas:(a)=>`💡 The answer was: "${a}"`,
    continueAnyway:"Continue anyway", continueBtn:"Continue",
    submitBtn:"SUBMIT", perfectResult:"✅ Perfect!", correctAns:(ans)=>`💡 Correct: ${ans}`,
    findBlockers:(n)=>`🔍 Find the 2 blockers! (${n}/2 found)`,
    matchLabel:(n,t)=>`🔗 Match each item on the left with its description on the right: (${n}/${t})`,
  },
  is: {
    cutTheCrap:"Sleppa leikjunum!", cutTheCrapTip:"Ég er annsaður, ég hef ekki tíma til að spila leiki!",
    heroTagline:"Hæ! Ég er Gaëtan. Kynstu ferli mínum — eitt stig í einu! 🇮🇸",
    heroSub:"GAGNVIRKT STARFSFERILS-ÆVINTÝRI",
    heroDesc:"Yfirmaður í upplýsingatækni · 15+ ár · 6 lönd · Full-stack manneskja 🧠",
    quickChallenge:"🎯 Hröð áskorun",
    spotOn:"Nákvæmlega rétt!", goodTryFull:"Góð tilraun — hér er heildarmyndin:", goodTryAgain:"Góð tilraun! Reyndu aftur til að opna heildarmyndina.",
    fullPicture:"✨ Heildarmyndin", skillsUnlocked:"🛠 HÆFNI OPNUÐ", toolsTech:"⚙️ TÆKI OG TÆKNI", funFact:"🎉 Gamalt staðreynd",
    playGame:"🔄 Spila leikinn",
    questComplete:"Leiðangur lokinn!",
    questDesc:"Þú hefur opnað fullt snið Gaëtans Goffe — sjaldgæfur blendingur djúprar tækniþekkingar og leiðtogafærni á æðsta stigi, mótaður á 15+ árum, 6 löndum og í greinum frá banka til lyfjaiðnaðar og skýjainnviða.",
    completeAll:"Ljúktu öllum stigum", completeDesc:(n,t)=>`Svaraðu áskorunum á hverju stigi til að afhjúpa fullt sniðið. ${n} / ${t} lokið.`,
    connectLinkedin:"TENGJAST Á LINKEDIN →", unlockConnect:"OPNAÐU TIL AÐ TENGJAST",
    footer:"Vefsíða þróuð í React.js með ást (og Claude). Myndir búnar til af NanoBanana.",
    clickOrder:"Smelltu á atburðina í tímaröð:",
    statement:(i,n)=>`FULLYRÐING ${i} / ${n}`,
    trueBtn:"RÉTT", falseBtn:"RANGT", allDone:(s,t)=>`✅ Lokið! ${s}/${t} rétt`,
    goBtn:"ÁFRAM", checkBtn:"ATHUGA", typeAnswer:"Sláðu inn svar…", unscrambleIt:"Raðaðu stöfunum…",
    hint:(h)=>`Vísbending: ${h}`, exactRight:"✅ Nákvæmlega rétt!", answerWas:(a)=>`💡 Svarið var: "${a}"`,
    continueAnyway:"Halda áfram", continueBtn:"Áfram",
    submitBtn:"SENDA", perfectResult:"✅ Fullkomið!", correctAns:(ans)=>`💡 Rétt: ${ans}`,
    findBlockers:(n)=>`🔍 Finndu 2 hindranirnar! (${n}/2 fundnar)`,
    matchLabel:(n,t)=>`🔗 Tengdu hvern lið til vinstri við lýsingu til hægri: (${n}/${t})`,
  },
};
const useT = () => { const l=useLang(); return (k,...a) => { const fn=UI[l]?.[k]??UI.en[k]; return typeof fn==="function"?fn(...a):fn??k; }; };

// ── RESPONSIVE HOOK ──────────────────────────────────────────
function useIsMobile() {
  const [mobile, setMobile] = useState(()=>window.innerWidth < 640);
  useEffect(()=>{
    const h = ()=>setMobile(window.innerWidth < 640);
    window.addEventListener("resize", h, {passive:true});
    return ()=>window.removeEventListener("resize", h);
  }, []);
  return mobile;
}

// ── GAËTAN ───────────────────────────────────────────────────
function Gaetan({ mood="happy", size=100, src="/gaetan_ref.png" }) {
  const f = { happy:"none", thinking:"grayscale(20%) brightness(.95)", sad:"grayscale(40%) brightness(.85)" };
  return (
    <img src={src} alt="Gaëtan Goffe"
      style={{width:size,height:size,borderRadius:"50%",filter:f[mood]||"none",display:"block",objectFit:"cover"}}/>
  );
}

// ── SPEECH BUBBLE ────────────────────────────────────────────
function Bubble({ children, color=P.yellow, pointUp=false, pointUpCenter=false }) {
  return (
    <div style={{position:"relative",background:color,border:`3px solid ${P.black}`,
      borderRadius:18,padding:"12px 18px",fontFamily:"Nunito,sans-serif",
      fontSize:15,fontWeight:700,color:P.black,maxWidth:360,lineHeight:1.4,
      boxShadow:`3px 3px 0 ${P.black}`}}>
      {children}
      {pointUp ? (
        <>
          <div style={{position:"absolute",top:-17,left:pointUpCenter?"calc(50% - 9px)":24,width:0,height:0,
            borderLeft:"9px solid transparent",borderRight:"9px solid transparent",
            borderBottom:`17px solid ${P.black}`}}/>
          <div style={{position:"absolute",top:-12,left:pointUpCenter?"calc(50% - 7px)":26,width:0,height:0,
            borderLeft:"7px solid transparent",borderRight:"7px solid transparent",
            borderBottom:`13px solid ${color}`}}/>
        </>
      ) : (
        <>
          <div style={{position:"absolute",left:-17,top:20,width:0,height:0,
            borderTop:"9px solid transparent",borderBottom:"9px solid transparent",
            borderRight:`17px solid ${P.black}`}}/>
          <div style={{position:"absolute",left:-12,top:22,width:0,height:0,
            borderTop:"7px solid transparent",borderBottom:"7px solid transparent",
            borderRight:`13px solid ${color}`}}/>
        </>
      )}
    </div>
  );
}

// ── REVEAL (scroll-triggered animation) ─────────────────────
function Reveal({ children, direction="up", delay=0 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(()=>{
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry])=>{
      if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, {threshold:0.12});
    obs.observe(el);
    return ()=>obs.disconnect();
  }, []);

  const transforms = {
    up:"translateY(40px)", left:"translateX(-60px)", right:"translateX(60px)",
    zoom:"scale(0.88)", flip:"perspective(500px) rotateX(20deg)",
  };

  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "none" : (transforms[direction]||"translateY(40px)"),
      transition: `transform 0.65s cubic-bezier(.16,1,.3,1) ${delay}ms, opacity 0.5s ease ${delay}ms`,
    }}>
      {children}
    </div>
  );
}

// ── QUIZ (MCQ) ───────────────────────────────────────────────
function Quiz({ q, opts, correctIdx, onDone, resetOnWrong=false }) {
  const [sel, setSel] = useState(null);
  const pick = i => {
    if (sel!==null) return;
    setSel(i);
    setTimeout(()=>{
      if (i === correctIdx) { onDone(true); }
      else if (resetOnWrong) { setSel(null); }
      else { onDone(false); }
    }, 900);
  };
  return (
    <div>
      <p style={{fontFamily:"Nunito",fontWeight:800,fontSize:15,color:P.white,marginBottom:14,lineHeight:1.45}}>{q}</p>
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {opts.map((o,i)=>{
          const done=sel!==null, right=i===correctIdx, wrong=done&&sel===i&&!right;
          return (
            <button key={i} onClick={()=>pick(i)} style={{
              background:done&&right?"#1a4a2a":wrong?"#4a1a1a":"rgba(255,255,255,0.07)",
              border:done&&right?`2px solid ${P.mint}`:wrong?`2px solid ${P.coral}`:"2px solid rgba(255,255,255,0.15)",
              borderRadius:12,padding:"11px 16px",textAlign:"left",cursor:done?"default":"pointer",
              fontFamily:"Nunito",fontWeight:700,fontSize:14,color:P.white,
              transition:"all .2s",display:"flex",justifyContent:"space-between",alignItems:"center",
            }}>
              <span>{o}</span>
              {done&&right&&<span style={{color:P.mint}}>✓</span>}
              {wrong&&<span style={{color:P.coral}}>✗</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── CLICK ORDER ──────────────────────────────────────────────
function ClickOrder({ items, correct, color, onDone }) {
  const [clicked, setClicked] = useState([]);
  const [flash, setFlash] = useState(null);

  const handleClick = (idx) => {
    if (clicked.includes(idx)) return;
    const step = clicked.length;
    if (idx === correct[step]) {
      const next = [...clicked, idx];
      setClicked(next);
      if (next.length === correct.length) setTimeout(()=>onDone(true), 600);
    } else {
      setFlash(idx);
      setTimeout(()=>{ setFlash(null); setClicked([]); }, 700);
    }
  };

  const t=useT();
  return (
    <div>
      <p style={{fontFamily:"Nunito",fontWeight:800,fontSize:14,color:P.white,marginBottom:12}}>
        {t("clickOrder")}
      </p>
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {items.map((item, idx)=>{
          const isClicked = clicked.includes(idx);
          const isFlashing = flash === idx;
          const order = clicked.indexOf(idx)+1;
          return (
            <button key={idx} onClick={()=>handleClick(idx)} style={{
              background: isClicked ? `${color}22` : isFlashing ? "#4a1a1a" : "rgba(255,255,255,0.07)",
              border: isClicked ? `2px solid ${color}` : isFlashing ? `2px solid ${P.coral}` : "2px solid rgba(255,255,255,0.15)",
              borderRadius:12, padding:"10px 14px", textAlign:"left",
              fontFamily:"Nunito", fontWeight:700, fontSize:13, color:P.white,
              transition:"all .2s", display:"flex", alignItems:"center", gap:10,
              cursor: isClicked ? "default" : "pointer",
            }}>
              {isClicked && <span style={{color,fontFamily:"Bangers",fontSize:18,minWidth:20}}>{order}</span>}
              {isFlashing && <span style={{color:P.coral,fontSize:16}}>✗</span>}
              {!isClicked && !isFlashing && <span style={{color:"rgba(255,255,255,0.3)",fontSize:12,minWidth:20}}>○</span>}
              <span>{item}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── TRUE / FALSE ─────────────────────────────────────────────
function TrueFalse({ items, color, onDone }) {
  const [idx, setIdx] = useState(0);
  const [choice, setChoice] = useState(null);
  const [done, setDone] = useState(false);
  const [score, setScore] = useState(0);

  const handle = (val) => {
    if (choice !== null) return;
    const correct = val === items[idx].a;
    if (correct) setScore(s=>s+1);
    setChoice(val);
    setTimeout(()=>{
      if (idx + 1 >= items.length) {
        setDone(true);
        setTimeout(()=>onDone(true), 500);
      } else {
        setIdx(i=>i+1);
        setChoice(null);
      }
    }, 1200);
  };

  const t=useT();
  if (done) return <div style={{fontFamily:"Nunito",color:P.mint,fontWeight:800,fontSize:15,textAlign:"center"}}>{t("allDone",score,items.length)}</div>;

  const item = items[idx];
  const isCorrect = choice === item.a;

  return (
    <div>
      <div style={{fontFamily:"Nunito",fontWeight:700,fontSize:12,color:color,marginBottom:8,letterSpacing:2}}>
        {t("statement",idx+1,items.length)}
      </div>
      <p style={{fontFamily:"Nunito",fontWeight:800,fontSize:15,color:P.white,marginBottom:14,lineHeight:1.5}}>
        {item.s}
      </p>
      {choice === null ? (
        <div style={{display:"flex",gap:12}}>
          <button onClick={()=>handle(true)} style={{
            flex:1, padding:"12px 0", borderRadius:12, border:`2px solid ${P.mint}55`,
            background:`${P.mint}15`, fontFamily:"Bangers", fontSize:22, color:P.mint, letterSpacing:2,
          }}>{t("trueBtn")}</button>
          <button onClick={()=>handle(false)} style={{
            flex:1, padding:"12px 0", borderRadius:12, border:`2px solid ${P.coral}55`,
            background:`${P.coral}15`, fontFamily:"Bangers", fontSize:22, color:P.coral, letterSpacing:2,
          }}>{t("falseBtn")}</button>
        </div>
      ) : (
        <div style={{
          background: isCorrect ? "rgba(6,214,160,0.12)" : "rgba(255,107,107,0.12)",
          border:`2px solid ${isCorrect ? P.mint : P.coral}55`,
          borderRadius:12, padding:"10px 14px",
          fontFamily:"Nunito", fontSize:13, color:P.white, fontWeight:700,
        }}>
          <span style={{fontSize:18}}>{isCorrect ? "✅" : "❌"}</span>{" "}
          {item.e}
        </div>
      )}
    </div>
  );
}

// ── FILL BLANK ───────────────────────────────────────────────
function FillBlank({ sentence, answer, hint, color, onDone }) {
  const [val, setVal] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [correct, setCorrect] = useState(false);
  const t=useT();

  const check = () => {
    const ok = val.trim().toLowerCase() === answer.toLowerCase();
    setCorrect(ok);
    setSubmitted(true);
    if (ok) setTimeout(()=>onDone(true), 800);
  };

  const parts = sentence.split("___");

  return (
    <div>
      <p style={{fontFamily:"Nunito",fontWeight:800,fontSize:15,color:P.white,marginBottom:14,lineHeight:1.6}}>
        {parts[0]}
        <span style={{display:"inline-block",borderBottom:`2px solid ${color}`,minWidth:80,
          textAlign:"center",color:submitted?(correct?P.mint:P.coral):color,
          fontWeight:800,padding:"0 4px"}}>
          {submitted ? (correct ? val : answer) : (val||"_____")}
        </span>
        {parts[1]}
      </p>
      {!submitted ? (
        <>
          <div style={{display:"flex",gap:8}}>
            <input value={val} onChange={e=>setVal(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&check()}
              placeholder={t("typeAnswer")}
              style={{
                flex:1, minWidth:0, background:"rgba(255,255,255,0.08)", border:`2px solid ${color}55`,
                borderRadius:10, padding:"10px 14px", color:P.white,
                fontFamily:"Nunito", fontSize:14, fontWeight:700, outline:"none",
              }}/>
            <button onClick={check} style={{
              flexShrink:0, background:color, color:P.black, borderRadius:10, padding:"10px 20px",
              fontFamily:"Bangers", fontSize:18, letterSpacing:1,
            }}>{t("goBtn")}</button>
          </div>
          <div style={{fontFamily:"Nunito",fontSize:12,color:P.muted,marginTop:6,fontStyle:"italic"}}>
            {t("hint",hint)}
          </div>
        </>
      ) : (
        <div style={{
          background: correct ? "rgba(6,214,160,0.12)" : "rgba(255,209,102,0.12)",
          border:`2px solid ${correct?P.mint:P.yellow}55`,
          borderRadius:12, padding:"10px 14px",
          fontFamily:"Nunito", fontSize:14, color:P.white, fontWeight:700,
        }}>
          {correct ? t("exactRight") : t("answerWas",answer)}
          {!correct && <button onClick={()=>onDone(true)} style={{marginLeft:12,color:color,fontFamily:"Nunito",fontWeight:700,fontSize:13,textDecoration:"underline"}}>{t("continueAnyway")}</button>}
        </div>
      )}
    </div>
  );
}

// ── MEMORY GAME ──────────────────────────────────────────────
function MemoryGame({ pairs, color, onDone }) {
  const cards = [...pairs.map((p,i)=>({id:i*2,   val:p.f, pairId:i})),
                 ...pairs.map((p,i)=>({id:i*2+1, val:p.b, pairId:i}))];
  const [deck] = useState(()=>[...cards].sort(()=>Math.random()-.5));
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [locked, setLocked] = useState(false);

  const flip = (card) => {
    if (locked || flipped.length===2 || flipped.find(c=>c.id===card.id) || matched.includes(card.pairId)) return;
    const next = [...flipped, card];
    setFlipped(next);
    if (next.length===2) {
      setLocked(true);
      if (next[0].pairId===next[1].pairId) {
        const newMatched = [...matched, next[0].pairId];
        setMatched(newMatched);
        setFlipped([]);
        setLocked(false);
        if (newMatched.length===pairs.length) setTimeout(()=>onDone(true),600);
      } else {
        setTimeout(()=>{ setFlipped([]); setLocked(false); }, 900);
      }
    }
  };

  return (
    <div>
      <p style={{fontFamily:"Nunito",fontWeight:800,fontSize:14,color:P.white,marginBottom:12}}>
        Match the pairs! Click to flip:
      </p>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
        {deck.map(card=>{
          const isFlipped = flipped.find(c=>c.id===card.id);
          const isMatched = matched.includes(card.pairId);
          const show = isFlipped || isMatched;
          return (
            <button key={card.id} onClick={()=>flip(card)} style={{
              height:72, borderRadius:10,
              background: show ? (isMatched ? `${color}22` : "rgba(255,255,255,0.12)") : `rgba(255,255,255,0.06)`,
              border: show ? `2px solid ${isMatched?color:"rgba(255,255,255,0.3)"}` : "2px solid rgba(255,255,255,0.12)",
              fontFamily:"Nunito", fontSize:show?12:22, fontWeight:700, color:P.white,
              transition:"all .25s", display:"flex", alignItems:"center", justifyContent:"center",
              padding:"4px", textAlign:"center", lineHeight:1.2,
            }}>
              {show ? card.val : "?"}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── WORD UNSCRAMBLE ──────────────────────────────────────────
function WordUnscramble({ scrambled, answer, hint, color, onDone }) {
  const [val, setVal] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [correct, setCorrect] = useState(false);
  const t=useT();

  const check = () => {
    const ok = val.trim().toUpperCase() === answer.toUpperCase();
    setCorrect(ok);
    setSubmitted(true);
    if (ok) setTimeout(()=>onDone(true), 800);
  };

  return (
    <div>
      <div style={{textAlign:"center",marginBottom:16}}>
        <div style={{fontFamily:"Bangers",fontSize:42,letterSpacing:8,color:color,
          textShadow:`3px 3px 0 ${P.black}`}}>
          {scrambled}
        </div>
        <div style={{fontFamily:"Nunito",fontSize:12,color:P.muted,marginTop:4,fontStyle:"italic"}}>
          {hint}
        </div>
      </div>
      {!submitted ? (
        <div style={{display:"flex",gap:8}}>
          <input value={val} onChange={e=>setVal(e.target.value.toUpperCase())}
            onKeyDown={e=>e.key==="Enter"&&check()}
            placeholder={t("unscrambleIt")}
            style={{
              flex:1, minWidth:0, background:"rgba(255,255,255,0.08)", border:`2px solid ${color}55`,
              borderRadius:10, padding:"10px 14px", color:P.white,
              fontFamily:"Bangers", fontSize:22, letterSpacing:4, outline:"none",
              textTransform:"uppercase",
            }}/>
          <button onClick={check} style={{
            flexShrink:0, background:color, color:P.black, borderRadius:10, padding:"10px 20px",
            fontFamily:"Bangers", fontSize:18, letterSpacing:1,
          }}>{t("checkBtn")}</button>
        </div>
      ) : (
        <div style={{
          background: correct ? "rgba(6,214,160,0.12)" : "rgba(255,209,102,0.12)",
          border:`2px solid ${correct?P.mint:P.yellow}55`,
          borderRadius:12, padding:"10px 14px",
          fontFamily:"Bangers", fontSize:24, color:correct?P.mint:P.yellow, letterSpacing:4,
          textAlign:"center",
        }}>
          {correct ? `✅ ${answer}!` : `💡 ${answer}`}
          {!correct && <button onClick={()=>onDone(true)} style={{display:"block",margin:"8px auto 0",color:color,fontFamily:"Nunito",fontWeight:700,fontSize:13,textDecoration:"underline"}}>{t("continueBtn")}</button>}
        </div>
      )}
    </div>
  );
}

// ── MULTI SELECT ─────────────────────────────────────────────
function MultiSelect({ question, options, correct, color, onDone }) {
  const [selected, setSelected] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const t=useT();

  const toggle = (i) => {
    if (submitted) return;
    setSelected(s => s.includes(i) ? s.filter(x=>x!==i) : [...s,i]);
  };

  const submit = () => {
    setSubmitted(true);
    setTimeout(()=>onDone(true), 800);
  };

  const isCorrectSet = submitted && JSON.stringify([...selected].sort()) === JSON.stringify([...correct].sort());

  return (
    <div>
      <p style={{fontFamily:"Nunito",fontWeight:800,fontSize:14,color:P.white,marginBottom:12}}>{question}</p>
      <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:14}}>
        {options.map((opt,i)=>{
          const sel = selected.includes(i);
          const isRight = correct.includes(i);
          const showRight = submitted && isRight;
          const showWrong = submitted && sel && !isRight;
          return (
            <button key={i} onClick={()=>toggle(i)} style={{
              background: showRight ? `${P.mint}22` : showWrong ? `${P.coral}22` : sel ? `${color}22` : "rgba(255,255,255,0.06)",
              border: showRight ? `2px solid ${P.mint}` : showWrong ? `2px solid ${P.coral}` : sel ? `2px solid ${color}` : "2px solid rgba(255,255,255,0.15)",
              borderRadius:20, padding:"7px 14px",
              fontFamily:"Nunito", fontWeight:700, fontSize:13, color:P.white,
              cursor:submitted?"default":"pointer", transition:"all .2s",
            }}>
              {showRight&&"✓ "}{showWrong&&"✗ "}{opt}
            </button>
          );
        })}
      </div>
      {!submitted ? (
        <button onClick={submit} disabled={selected.length===0} style={{
          background: selected.length>0 ? color : "rgba(255,255,255,0.1)",
          color: selected.length>0 ? P.black : P.muted,
          borderRadius:10, padding:"10px 24px",
          fontFamily:"Bangers", fontSize:18, letterSpacing:1,
          cursor:selected.length>0?"pointer":"default",
        }}>{t("submitBtn")}</button>
      ) : (
        <div style={{fontFamily:"Nunito",fontSize:13,color:P.mint,fontWeight:700}}>
          {isCorrectSet ? t("perfectResult") : t("correctAns",correct.map(i=>options[i]).join(", "))}
        </div>
      )}
    </div>
  );
}

// ── BLOCKER HUNT ─────────────────────────────────────────────
function MatchPairs({ pairs, color, onDone }) {
  const [rightOrder] = useState(()=>[...pairs.map((_,i)=>i)].sort(()=>Math.random()-.5));
  const [selected, setSelected] = useState(null);
  const [matched, setMatched] = useState({});
  const [wrongRight, setWrongRight] = useState(null);

  const matchedRights = new Set(Object.values(matched));
  const total = pairs.length;

  const clickLeft = (i) => {
    if (matched[i]!==undefined) return;
    setSelected(i===selected ? null : i);
  };

  const clickRight = (ri) => {
    if (matchedRights.has(ri)) return;
    if (selected===null) return;
    if (rightOrder[ri]===selected) {
      const next = {...matched, [selected]:ri};
      setMatched(next);
      setSelected(null);
      if (Object.keys(next).length===total) setTimeout(()=>onDone(true),600);
    } else {
      setWrongRight(ri);
      setTimeout(()=>{ setWrongRight(null); setSelected(null); },700);
    }
  };

  return (
    <div>
      <p style={{fontFamily:"Nunito",fontWeight:800,fontSize:14,color:P.white,marginBottom:12}}>
        🔗 Match each item on the left with its description on the right: ({Object.keys(matched).length}/{total})
      </p>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {pairs.map((p,i)=>{
            const isMatched=matched[i]!==undefined, isSel=selected===i;
            return (
              <button key={i} onClick={()=>clickLeft(i)} style={{
                background:isMatched?`${color}22`:isSel?`${color}33`:"rgba(255,255,255,0.06)",
                border:isMatched?`2px solid ${color}`:isSel?`2px solid ${color}`:"2px solid rgba(255,255,255,0.15)",
                borderRadius:10, padding:"10px 12px", textAlign:"left",
                fontFamily:"Nunito", fontWeight:700, fontSize:13, color:P.white,
                transition:"all .2s", cursor:isMatched?"default":"pointer",
                boxShadow:isSel?`0 0 0 3px ${color}55`:"none",
              }}>
                {isMatched&&<span style={{marginRight:6,color}}>✓</span>}{p.left}
              </button>
            );
          })}
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {rightOrder.map((origIdx,ri)=>{
            const isMatched=matchedRights.has(ri), isWrong=wrongRight===ri;
            return (
              <button key={ri} onClick={()=>clickRight(ri)} style={{
                background:isMatched?`${color}22`:isWrong?"rgba(255,107,107,0.18)":"rgba(255,255,255,0.06)",
                border:isMatched?`2px solid ${color}`:isWrong?`2px solid ${P.coral}`:"2px solid rgba(255,255,255,0.15)",
                borderRadius:10, padding:"10px 12px", textAlign:"left",
                fontFamily:"Nunito", fontWeight:700, fontSize:13, color:P.white,
                transition:"all .2s", cursor:isMatched?"default":"pointer",
              }}>
                {isMatched&&<span style={{marginRight:6,color}}>✓</span>}{pairs[origIdx].right}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function BlockerHunt({ tickets, blockers, color, onDone }) {
  const [found, setFound] = useState([]);
  const [wrong, setWrong] = useState([]);

  const click = (i) => {
    if (found.includes(i) || wrong.includes(i)) return;
    if (blockers.includes(i)) {
      const next = [...found, i];
      setFound(next);
      if (next.length === blockers.length) setTimeout(()=>onDone(true), 600);
    } else {
      setWrong(w=>[...w,i]);
      setTimeout(()=>setWrong(w=>w.filter(x=>x!==i)), 700);
    }
  };

  const statusColors = {"IN PROGRESS":"#FFB347","TO DO":"#74C7EC","DONE":"#06D6A0","BLOCKED":"#FF6B6B","IN REVIEW":"#B8A9FF"};
  const ui=useT();

  return (
    <div>
      <p style={{fontFamily:"Nunito",fontWeight:800,fontSize:14,color:P.white,marginBottom:12}}>
        {ui("findBlockers",found.length)}
      </p>
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {tickets.map((t,i)=>{
          const isFound = found.includes(i);
          const isWrong = wrong.includes(i);
          return (
            <button key={i} onClick={()=>click(i)} style={{
              background: isFound ? "rgba(6,214,160,0.12)" : isWrong ? "rgba(255,107,107,0.18)" : "rgba(255,255,255,0.05)",
              border: isFound ? `2px solid ${P.mint}` : isWrong ? `2px solid ${P.coral}` : "2px solid rgba(255,255,255,0.12)",
              borderRadius:10, padding:"10px 14px", textAlign:"left",
              fontFamily:"Nunito", fontWeight:700, fontSize:13, color:P.white,
              transition:"all .2s", display:"flex", alignItems:"center", gap:12,
              cursor: isFound ? "default" : "pointer",
            }}>
              <span style={{fontFamily:"Bangers",fontSize:11,letterSpacing:1,color:P.muted,flexShrink:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.id}</span>
              <span style={{flex:1,minWidth:0}}>{t.title}</span>
              <span style={{
                fontFamily:"Bangers",fontSize:11,letterSpacing:1,
                color:statusColors[t.status]||P.muted,
                background:`${statusColors[t.status]||P.muted}22`,
                borderRadius:6, padding:"2px 8px",
                flexShrink:0, whiteSpace:"nowrap",
              }}>{t.status}</span>
              {isFound && <span style={{color:P.mint}}>🚨</span>}
              {isWrong && <span style={{color:P.coral}}>✗</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── COMPLETION BURST ─────────────────────────────────────────
function CompletionBurst({ levelId }) {
  const [alive, setAlive] = useState(true);
  const [entered, setEntered] = useState(false);
  const [fading, setFading] = useState(false);

  useEffect(()=>{
    const t0 = setTimeout(()=>setEntered(true), 40);
    if (levelId === 5) {
      const t1 = setTimeout(()=>setFading(true), 3000);
      const t2 = setTimeout(()=>setAlive(false), 4600);
      return ()=>{ clearTimeout(t0); clearTimeout(t1); clearTimeout(t2); };
    }
    const t = setTimeout(()=>setAlive(false), 3200);
    return ()=>{ clearTimeout(t0); clearTimeout(t); };
  }, [levelId]);

  if (!alive) return null;

  if (levelId === 1) {
    return (
      <div style={{position:"fixed",inset:0,zIndex:999,pointerEvents:"none"}}>
        {[0,1,2,3,4,5].map(i=>(
          <span key={i} style={{
            position:"absolute", bottom:60, left:`${15+i*14}%`,
            fontSize:42, animation:`burst-up 1.8s ease-out ${i*200}ms both`,
          }}>🧇</span>
        ))}
      </div>
    );
  }
  if (levelId === 2) {
    const angles = [-40,-25,-12,0,12,25,40,55];
    return (
      <div style={{position:"fixed",inset:0,zIndex:999,pointerEvents:"none"}}>
        {angles.map((a,i)=>(
          <span key={i} style={{
            position:"absolute", bottom:60, left:"50%",
            fontSize:36, transformOrigin:"bottom center",
            animation:`burst-up 1.6s ease-out ${i*120}ms both`,
            marginLeft:`${(i-3.5)*40}px`,
          }}>🌷</span>
        ))}
      </div>
    );
  }
  if (levelId === 3) {
    return (
      <div style={{position:"fixed",inset:0,zIndex:999,pointerEvents:"none"}}>
        {Array.from({length:12},(_,i)=>(
          <span key={i} style={{
            position:"absolute",
            top:`${20+Math.random()*60}%`,
            left:`${10+Math.random()*80}%`,
            fontSize:28,
            animation:`sparkle-pop 1.2s ease-in-out ${i*180}ms both`,
          }}>⭐</span>
        ))}
      </div>
    );
  }
  if (levelId === 4) {
    const colors = ["#FF0000","#0000FF","#FFFFFF","#FF0000","#0000FF"];
    return (
      <div style={{position:"fixed",inset:0,zIndex:999,pointerEvents:"none"}}>
        {Array.from({length:20},(_,i)=>(
          <div key={i} style={{
            position:"absolute", top:-20,
            left:`${Math.random()*100}%`,
            width:10, height:10,
            borderRadius:2,
            background:colors[i%colors.length],
            animation:`confetti-fall ${1.5+Math.random()*1.5}s linear ${i*120}ms both`,
          }}/>
        ))}
      </div>
    );
  }
  if (levelId === 5) {
    const strips = [
      {c:"#06D6A0", top:"18%", h:70,  anim:"aurora",  dur:"2.8s", delay:"0ms"},
      {c:"#B8A9FF", top:"36%", h:90,  anim:"aurora2", dur:"3.4s", delay:"300ms"},
      {c:"#74C7EC", top:"55%", h:60,  anim:"aurora",  dur:"2.5s", delay:"600ms"},
      {c:"#06D6A0", top:"72%", h:50,  anim:"aurora2", dur:"3.1s", delay:"150ms"},
    ];
    return (
      <div style={{
        position:"fixed", inset:0, zIndex:999, pointerEvents:"none",
        opacity: fading ? 0 : entered ? 1 : 0,
        transition: fading ? "opacity 1.6s ease-out" : "opacity 1.4s ease-in",
      }}>
        {strips.map((s,i)=>(
          <div key={i} style={{
            position:"absolute", left:0, right:0,
            top:s.top, height:s.h,
            background:s.c, opacity:0.75,
            animation:`${s.anim} ${s.dur} ease-in-out ${s.delay} infinite`,
            filter:"blur(26px)",
          }}/>
        ))}
      </div>
    );
  }
  if (levelId === 6) {
    return (
      <div style={{position:"fixed",inset:0,zIndex:999,pointerEvents:"none"}}>
        {[0,1,2,3,4].map(i=>(
          <span key={i} style={{
            position:"absolute", bottom:60, left:`${10+i*18}%`,
            fontSize:38, animation:`burst-up 1.8s ease-out ${i*200}ms both`,
          }}>🍺</span>
        ))}
        <span style={{
          position:"absolute", bottom:80, left:"50%", transform:"translateX(-50%)",
          fontSize:52, animation:"burst-up 2s ease-out 800ms both",
        }}>🏰</span>
      </div>
    );
  }
  if (levelId === 7) {
    const sci = ["🔬","💊","🧬","🔬","💊","🧬","🔬","💊","🧬","🔬"];
    return (
      <div style={{position:"fixed",inset:0,zIndex:999,pointerEvents:"none"}}>
        {sci.map((s,i)=>(
          <span key={i} style={{
            position:"absolute", bottom:40, left:`${5+i*10}%`,
            fontSize:30, animation:`bubble-rise 2s ease-out ${i*200}ms both`,
          }}>{s}</span>
        ))}
      </div>
    );
  }
  if (levelId === 8) {
    return (
      <div style={{position:"fixed",inset:0,zIndex:999,pointerEvents:"none"}}>
        <img src="/bonhomme.png" alt="" style={{
          position:"absolute", bottom:80, height:80,
          animation:"bonhomme-sprint 2s ease-in-out forwards",
        }}/>
        {[0,1,2,3,4].map(i=>(
          <span key={i} style={{
            position:"absolute", bottom:100, left:`${10+i*20}%`,
            fontSize:20, animation:`burst-up 1s ease-out ${i*300}ms both`,
          }}>💨</span>
        ))}
      </div>
    );
  }
  return null;
}

// ── LEVELS DATA ──────────────────────────────────────────────
const LEVELS = [
  {
    id:1, emoji:"🍟", title:"The Origin",
    period:"2006 – 2011", company:"Callataÿ & Wouters", city:"Brussels, Belgium",
    color:P.coral, bg:"#0c1210",
    teaser:"A biochemist walks into a bank software company. No IT degree. 6 months to prove himself.",
    facts:[
      "Self-taught COBOL, SQL & Linux — from scratch",
      "Started in L2 support, grew into front-end dev (Java / Spring / J2EE)",
      "Became domain expert in account management & deposits",
    ],
    bonusFacts:[
      "Progressed to Team Leader without a formal IT background",
      "Had a full-stack view of the software - back-end in Cobol and front-end in Java",
    ],
    skills:["Self-directed learning","COBOL / SQL / Linux","Java / Spring / J2EE","Domain expertise","Team leadership"],
    tools:["COBOL","SQL","Java","Spring","J2EE","Linux"],
    gameType:"quiz",
    q:"Gaëtan had 6 months to get up to speed. What did he teach himself first?",
    opts:["Python & React","COBOL, SQL & Linux","PHP & JavaScript","C++ & Assembly"],
    correct:1,
    funFact:"His scientific mindset — systematic observation & hypothesis testing — turned out to be perfect for debugging legacy banking code. 🔬",
    is:{
      teaser:"Lífefnafræðingur gengur inn í hugbúnaðarfyrirtæki fyrir banka. Ekkert upplýsingatæknipróf. 6 mánuðir til að sanna sig.",
      facts:["Lærði COBOL, SQL og Linux sjálfur — frá grunni","Byrjaði í L2 stuðningi, þróaðist í framendaforritun (Java / Spring / J2EE)","Varð sérfræðingur í reikningsstjórnun og innlán"],
      bonusFacts:["Hlaut hlutverk liðsstjóra án formlegs upplýsingatækniprófs","Hafði heildarútsýni yfir hugbúnaðinn — bakendi í COBOL og framendi í Java"],
      funFact:"Vísindaleg hugsunarháttur hans — kerfisbundin athugun og tilgátuprófun — reyndist fullkominn fyrir villuleit í gamalli bankaforritun. 🔬",
      q:"Gaëtan hafði 6 mánuði til að koma sér inn. Hvað lærði hann sjálfur fyrst?",
      opts:["Python og React","COBOL, SQL og Linux","PHP og JavaScript","C++ og Assembly"],
    },
  },
  {
    id:2, emoji:"⚙️", title:"The Windmill",
    period:"2011 – 2013", company:"Callataÿ & Wouters / Sopra Banking Software", city:"The Hague, Netherlands",
    portrait:"/gaetan_nl.png", color:P.sky, bg:"#001220",
    teaser:"Two banks. Nine months. One person on-site. What could go wrong?",
    facts:[
      "Sole on-site contact for a Dutch banking client while the full team stayed in Brussels",
      "Led many blueprint workshops — mapping hundreds of requirements to the solution",
      "Quality issues after first delivery → built a full regression testing framework from scratch",
    ],
    bonusFacts:[
      "Launched two banks (one in Belgium and one in The Netherlands) in a record timeline: 9 months!",
      "Wore 3 hats: Team Leader, Solution Architect & Test Manager",
      "Prince2 Practitioner certified (2013)",
    ],
    skills:["Discovery workshops","Regression testing","Multi-role delivery","Client management","Prince2"],
    tools:["Prince2","VBA","Shell scripting", "Test frameworks"],
    gameType:"clickorder",
    coItems:["Built regression testing framework from scratch","Launched Dutch bank (big-bang migration)","Discovery workshops — all requirements","Development frenzy!"],
    coCorrect:[2,3,0,1],
    funFact:"The Belgian bank went live just before Christmas 2011. His manager called it 'the best Christmas gift the bank could get.' 🎄",
    is:{
      teaser:"Tveir bankar. Níu mánuðir. Ein manneskja á staðnum. Hvað gæti farið úrskeiðis?",
      facts:["Eini fulltrúi á staðnum hjá hollensku bankaviðskiptavininum á meðan allt liðið var í Brussel","Leiddi marga undirbúningsverkstæði — kortlagði hundruð af kröfum á lausnina","Gæðavandamál eftir fyrstu afhendingu → smíðaði fullkominn prófunarramma frá grunni"],
      bonusFacts:["Opnaði tvo banka (einn í Belgíu og einn í Hollandi) á metlangri tímalínu: 9 mánuðir!","Fór í þrjár hlutverksbrautir samtímis: liðsstjóri, lausnararkítekt og prófunastjóri","Prince2 Practitioner vottun (2013)"],
      funFact:"Belgíski bankinn fór í gagnið rétt fyrir jól 2011. Yfirmaður hans kallaði það 'besta jólagjafarinn sem bankinn gæti fengið.' 🎄",
      coItems:["Smíðaði prófunarramma frá grunni","Opnaði hollensku bankann (stórskiptiflutningur)","Undirbúningsverkstæði — allar kröfur","Þróunargleyðingur!"],
    },
  },
  {
    id:3, emoji:"🗼", title:"The Tower",
    period:"2013 – 2016", company:"Sopra Banking Software", city:"Paris, France",
    portrait:"/gaetan_fr.png", color:P.peach, bg:"#0a1428",
    teaser:"One of France's largest banks. A 100-person programme. A zOS mainframe. Countless stakeholders. Delivered with composure.",
    facts:[
      "PM on a massive IT overhaul for one of France's largest banks.",
      "Legacy system: zOS & mainframe — hundreds of integrations to untangle",
      "Managed a complex, sometimes hostile, multi-stakeholder environment with rigour and composure",
      "Was fully managing a team for the first time: defining individual objectives and conducting frequent performance interviews"
    ],
    bonusFacts:[
      "No custom dev allowed — all gaps had to go through R&D for the standard product",
      "Team of 5–10 within a ~100-person programme",
      "Automated its project reporting using Excel dashboards with VBA macros — no PM tool was provided",
      "ITIL Foundation v3 certified (2016)",
    ],
    skills:["Programme management","HR Management","Stakeholder management","zOS / mainframe","Excel VBA automation","ITIL v3","resilience","composure"],
    tools:["Excel + VBA", "Sharepoint","PowerPoint","ITIL"],
    gameType:"truefalse",
    tfItems:[
      {s:"The programme used Agile ceremonies",a:false,e:"Pure Waterfall — formal approvals, structured change management"},
      {s:"All gaps had to be resolved through standard R&D product",a:true,e:"No custom dev allowed — every gap went through workshops with R&D"},
      {s:"Gaëtan automated its reporting using VBA macros",a:true,e:"No PM tool was provided — dashboards, budgets and risk logs all in Excel"},
    ],
    funFact:"The client was at times outright hostile — tense meetings, aggressive pushback, confrontational exchanges. Gaëtan's response was always the same: document everything, keep every interaction structured, never escalate emotionally. He later called this chapter his crash course in stakeholder resilience. 🧘",
    is:{
      teaser:"Einn af stærstu bönkum Frakklands. 100 manna forrit. zOS stórkerfi. Ótaldir hagsmunaaðilar. Afhent með kyrrð.",
      facts:["Verkefnisstjóri á stórum upplýsingatæknibreytingum hjá einum af stærstu bönkum Frakklands.","Eldra kerfi: zOS og stórkerfi — hundruð samþættinga að leysa","Stjórnaði flóknu, stundum fjandsamlegu, umhverfi margra hagsmunaaðila með nákvæmni og kyrrð","Stjórnaði liði í fyrsta sinn: setti einstaklingsmarkmið og framkvæmdi tíð frammistöðuviðtöl"],
      bonusFacts:["Engin sérsniðin þróun leyfð — allar eyður þurftu að fara í gegnum R&D","Lið 5–10 innan ~100 manna forrits","Sjálfvirknittti verkefnaskýrslugjöf með Excel mælaborðum með VBA — ekkert PM tól var gefið","ITIL Foundation v3 vottun (2016)"],
      funFact:"Viðskiptavinurinn var stundum beinlínis fjandsamlegur — spenntir fundir, árásargjörn mótmæli, átakafull samskipti. Viðbrögð Gaëtans voru alltaf þau sömu: skráðu allt, haldið hverri samskiptum skipulagi, stigmagnið aldrei tilfinningalega. Þennan kafla kallaði hann crash course í þoli hagsmunaaðila. 🧘",
      tfItems:[
        {s:"Forritið notaði Agile hátíðir",a:false,e:"Hreinn Waterfall — formlegar samþykktir, skipulegar breytingastjórnun"},
        {s:"Allar eyður þurftu að leysa sig í gegnum staðlað R&D vöru",a:true,e:"Engin sérsniðin þróun leyfð — allar eyður fóru í gegnum verkstæði með R&D"},
        {s:"Gaëtan sjálfvirknittti skýrslugjöf sína með VBA macros",a:true,e:"Ekkert PM tól var gefið — mælaborð, fjárhagsáætlanir og áhættuyfirlit allt í Excel"},
      ],
    },
  },
  {
    id:4, emoji:"👑", title:"The Crown",
    period:"2016 – 2018", company:"Sopra Banking Software", city:"London, United Kingdom",
    portrait:"/gaetan_uk.png", color:P.mint, bg:"#001a0d",
    teaser:"Build a bank from scratch in London. The product was ready. The licence never came.",
    facts:[
      "Deputy programme manager on a greenfield UK digital bank launch",
      "Arrived on a project already at risk, rewrote the PID and redesigned delivery from scratch",
      "SaaS 'model bank' on private cloud (+ AWS proof of concept)",
    ],
    bonusFacts:[
      "Multiple banking licence failures → presented continuation options to the bank C-level each time",
      "Completed budgetary objectives of the project above expectations",
      "Parallel: Led a discovery project to simplify the Loan Origination process for the biggest bank of Scotland + multiple bid management",
      "Agile PM Practitioner certified (2018)",
    ],
    skills:["Bid management","Budget management","Risk management","Greenfield delivery","SaaS","Executive reporting","Agile PM"],
    tools:["Cloud","MS Project","Confluence","Trello"],
    gameType:"fillblank",
    fbSentence:"The product was fully built, tested, client-approved. The bank never obtained its ___.",
    fbAnswer:"licence",
    fbHint:"Sometimes the blocker isn't technical.",
    funFact:"Even as a Senior PM on a ~40-person programme, Gaëtan still rolled up his sleeves to write code and debug when the project called for it. A PM who can read the code — that's a rare find. 💻",
    is:{
      teaser:"Byggðu banka frá grunni í London. Varan var tilbúin. Leyfið kom aldrei.",
      facts:["Varaliðsstjóri forrits á nýrri braut stafræns banka í Bretlandi","Kom inn á verkefni sem var þegar í hættu, endurskrifaði PID og endurhannaði afhendingu frá grunni","SaaS 'líkanabanki' á einkaskýi (+ AWS proof of concept)"],
      bonusFacts:["Margar leyfisskylur bankans mistókust → kynnti framhaldsmöguleika til æðstu stjórnenda bankans í hvert skipti","Ljúkti fjárhagslegum markmiðum verkefnisins yfir væntingum","Samhliða: Leiddi uppgötvunarverkefni til að einfalda lánsupphafsferlið fyrir stærsta banka Skotlands","Agile PM Practitioner vottun (2018)"],
      funFact:"Jafnvel sem yfir verkefnisstjóri á ~40 manna forrit, rúllaði Gaëtan enn upp ermarnar og skrifaði kóða þegar þurfa þótti. Verkefnisstjóri sem getur lesið kóðann — það er sjaldgæft. 💻",
      fbSentence:"Varan var fullbyggð, prófuð og samþykkt af viðskiptavininum. Bankinn fékk aldrei sitt ___.",
      fbAnswer:"leyfi",
      fbHint:"Stundum er hindrunin ekki tæknileg.",
    },
  },
  {
    id:5, emoji:"🌌", title:"The Northern Lights",
    period:"2018 – 2021", company:"Sopra Banking Software @ RB", city:"Reykjavik, Iceland",
    portrait:"/gaetan_is.png", color:P.lavender, bg:"#08001a",
    teaser:"6 Icelandic banks. 1 pandemic. Everyone aligned. Big-bang migration: March 2021.",
    facts:[
      "Embedded at Reiknistofa Bankanna (RB) — IT backbone for all Icelandic banks, multiple parallel streams",
      "Supported Arion Banki migration: SBS, RB and client teams kept aligned throughout via clear structure and communication",
      "Project survived a months-long COVID pause — collaboration and trust never wavered, migration completed on schedule",
    ],
    bonusFacts:[
      "Client had never managed a project at this scale — supported them from day one, blueprint to go-live",
      "Parallel streams: savings banks, Central Bank of Iceland (Seðlabanki Íslands), Indo Banki onboarding",
      "Personal achievement during COVID: built an Android app in Java & Kotlin with Firebase — cataloguing his comic book collection",
      "Also first steps in web dev: HTML, CSS and JavaScript under the Vue.js framework",
    ],
    skills:["Multi-party coordination","Client coaching","Risk management","Multi-project leadership"],
    tools:["MS Project","Excel","Jira","Confluence","PowerPoint"],
    gameType:"memory",
    memPairs:[
      {f:"🏦",b:"Arion Banki"},
      {f:"💥",b:"Big-bang migration"},
      {f:"🦠",b:"COVID pause survived"},
      {f:"🇮🇸",b:"6 Icelandic banks"},
    ],
    funFact:"The big-bang migration happened on a weekend night — the entire Icelandic banking system stopped, migrated, restarted. As simple as that...",
    is:{
      teaser:"6 íslensku bankar. 1 heimsfaraldur. Allir sammála. Stórskiptiflutningur: mars 2021.",
      facts:["Innleiddur hjá Reiknistofu Bankanna (RB) — upplýsingatæknigrunni allra íslenskra banka, margar samhliða lotur","Stuðlaði að Arion Banka flutningi: SBS, RB og lið viðskiptavinar haldið samstilltum í gegnum skýra uppbyggingu og samskipti","Verkefnið lifði af mánuðalangan COVID hlé — samstarf og traust brá aldrei, flutningur kláraðist á réttum tíma"],
      bonusFacts:["Viðskiptavinurinn hafði aldrei stjórnað verkefni á þessum skala — stuðlaði þeim frá fyrsta degi, frá undirbúningi að gangsetningu","Samhliða lotur: sparisjóðir, Seðlabanki Íslands, Indo Banki innleiðing","Persónuleg afrek á COVID tímanum: smíðaði Android app í Java og Kotlin með Firebase","Einnig fyrstu skref í vefþróun: HTML, CSS og JavaScript undir Vue.js ramma"],
      funFact:"Stórskiptiflutningurinn gerðist á helgarkvöldi — allt íslenska bankakerfið stoppaði, fluttist, endurræstist. Svona einfalt...",
      memPairs:[
        {f:"🏦",b:"Arion Banki"},
        {f:"💥",b:"Stórskiptiflutningur"},
        {f:"🦠",b:"COVID hlé lifað af"},
        {f:"🇮🇸",b:"6 íslensku bankar"},
      ],
    },
  },
  {
    id:6, emoji:"🏰", title:"The Castle",
    period:"2021–2022", company:"Sopra Banking Software", city:"Nürnberg, Germany",
    portrait:"/gaetan_de.png", color:"#D4A017", bg:"#1a1200",
    teaser:"7 German banks. Huge stakes. A vision to build. A team to unite.",
    facts:[
      "Programme Manager for the first delivery phase: cards, legal reporting, customer onboarding",
      "Led 3 project managers — ~50 people in Phase 1",
      "Partnership with newly-acquired Sopra Financial Technology (SFT)",
    ],
    bonusFacts:[
      "Highest-stakes programme of the career: several billion euros in the prospect",
      "Navigated deep SBS/SFT cultural differences — two companies, two working styles, one shared goal",
      "Simultaneously led three projects across Phase 1",
      "Built trust between SBS and SFT teams at a critical moment in the company merger",
    ],
    skills:["Programme Management","Multi-PM leadership","Cross-cultural management","Executive communication","High-level oversight"],
    tools:["MS Project","Jira", "MS Teams","Confluence"],
    gameType:"unscramble",
    unscrambleWord:"DIPLOMACY",
    unscrambleHint:"He navigated two company cultures with this quality.",
    unscrambleScrambled:"ACYOPLMID",
    funFact:"Leading 50 people across 7 German banks at the intersection of two company cultures — this chapter proved that technical skill alone is never enough. Human communication is the force multiplier. 🏰",
    is:{
      teaser:"7 þýskir bankar. Miklar áhyggjur. Framtíðarsýn til að byggja. Lið til að sameina.",
      facts:["Forritsstjóri fyrir fyrsta afhendingarstig: kort, lagaleg skýrslugjöf, viðskiptavinaskráning","Leiddi 3 verkefnisstjóra — ~50 manns á stigi 1","Samstarf við nýkeyptan Sopra Financial Technology (SFT)"],
      bonusFacts:["Gengisríkasta forritið á ferilnum: nokkrar milljarðar evra í möguleikum","Sigldi í gegnum djúpar menningarmunur SBS/SFT — tvö fyrirtæki, tvær vinnubrautir, eitt sameiginlegt markmið","Leiddi þrjú verkefni samtímis yfir stig 1","Byggði traust milli SBS og SFT liðanna á mikilvægum tíma í samruna fyrirtækisins"],
      funFact:"Að leiða 50 manns yfir 7 þýska banka á mótum tveggja fyrirtækjamenninga — þessi kafli sannfærði að tæknileg færni ein er aldrei nóg. Mannleg samskipti eru kraftmagnaðari. 🏰",
      unscrambleHint:"Hann sigldi milli tveggja fyrirtækjamenninga með þennan eiginleika.",
    },
  },
  {
    id:7, emoji:"💨", title:"The Geyser",
    period:"2022 – 2024", company:"Alvotech", city:"Reykjavik, Iceland",
    portrait:"/gaetan_al.png", color:P.yellow, bg:"#0d1a00",
    teaser:"First time on the client side. A SAP timeline to challenge. A LIMS to rescue. GMP compliance throughout.",
    facts:[
      "First customer-side role — Digital Transformation team building AI-ready data foundations in a regulated pharma environment",
      "SAP S/4HANA migration: joined a project highly at risk, recommended C-level Steering Committee to reset the project with another supplier after rapid assessment of the situation",
      "Rescued a troubled LIMS implementation — tracked in Jira, delivered on time through rigorous UAT",
      "Familiarized with Power BI · Power Automate · Miro ·  Veeva QMS (GMP regulatory compliance)",
    ],
    bonusFacts:[
      "Familiarised rapidly with GMP standards — first pharma/biomedical environment",
      "Joined the R&D Operational Excellence team: step-by-step process reviews with end users",
      "PMP certified (2023) — obtained during this role",
      "His independent SAP analysis proved accurate — a reminder that rapid, evidence-based assessment is itself a deliverable. ♟",
    ],
    skills:["Digital transformation","GMP compliance","LIMS implementation","Data-driven decision making","PMP"],
    tools:["Power BI","Power Automate","LIMS","Jira","Miro","Confluence","Veeva QMS"],
    gameType:"multiselect",
    msQ:"Which tools did Gaëtan use at Alvotech? (pick all that apply)",
    msOpts:["Power BI","COBOL","Jira","Veeva QMS","Power Automate","SmartSheet","Trello"],
    msCorrect:[0,2,3,4],
    funFact:"Rescuing a LIMS project no one believed in, delivering a rapid SAP supplier assessment that proved right, simplifying and strenghtening processes in a GMP environment — this chapter proved that structured analysis and domain expertise speak for themselves. 🧬",
    is:{
      teaser:"Í fyrsta skipti á hliðar viðskiptavinar. SAP tímabil til að gagnrýna. LIMS til að bjarga. GMP samræmni í gegnum allt.",
      facts:["Fyrsta hlutverk á hliðar viðskiptavinar — stafrænt umbreytingarlið sem byggir grunnstoðir fyrir gervigreind í stjórnuðu lyfjaumhverfi","SAP S/4HANA flutningur: blandaðist við verkefni mjög í hættu, mælt með C-level stýrinefndinni að endurstilla verkefnið með öðrum birgi","Bjargaði eirðarlaustum LIMS innleiðingu — rakinn í Jira, afhent á réttum tíma í gegnum strangt UAT","Kynntist Power BI · Power Automate · Miro · Veeva QMS (GMP regllusamræmni)"],
      bonusFacts:["Kynntist GMP stöðlum hratt — fyrsta lyfja/lífrænt umhverfi","Blandaðist við R&D Operational Excellence liðið: skref-fyrir-skref ferlisfar með lokanotendum","PMP vottun (2023) — fengin á þessum starfi","Sjálfstæð SAP greining hans reyndist nákvæm — minnir á að hraðmat byggt á gögnum er sjálft afhending. ♟"],
      funFact:"Að bjarga LIMS verkefni sem enginn trúði á, skila hraðmati á SAP birgi sem reyndist rétt, einfalda ferla í GMP umhverfi — þessi kafli sýndi að skipulegt mat og sérþekking tala fyrir sig. 🧬",
      msQ:"Hvaða tæki notaði Gaëtan hjá Alvotech? (veldu allt sem á við)",
    },
  },
  {
    id:8, emoji:"🏔", title:"The Fjord",
    period:"2024 – 2025", company:"NetApp", city:"Reykjavik, Iceland",
    portrait:"/gaetan_ne.png", color:P.sky, bg:"#00101a",
    teaser:"8 Agile teams. Azure. Google Cloud. One dashboard to rule them all.",
    facts:[
      "Senior Engineering Program Manager coordinating teams across Iceland, California & India",
      "Focus: Azure NetApp Files (ANF) on Microsoft Azure & Google GCP",
      "Arrived to find 8 teams using Jira differently — no shared visibility",
    ],
    bonusFacts:[
      "Built a unified JQL dashboard: single view of all teams, early risk detection",
      "Introduced weekly stand-up across all team leads",
      "Direct interface between engineering, NetApp Product Managers and Microsoft",
      "Role relocated to the US in November 2025. Since then he enjoyed deeply generating content and automating tasks with AI tools.",
    ],
    skills:["Engineering programme management","Jira / JQL","Agile at scale","Cross-timezone coordination","Azure / GCP"],
    tools:["Jira / JQL","Azure","GCP","Confluence","PowerPoint","AI and Automation Tools (Claude/Nano Banana/Make/N8N)"],
    gameType:"blockerhunt",
    bhTickets:[
      {id:"ANF-1247",title:"SDK migration — unresolved dependency halt",  status:"BLOCKED"},
      {id:"ANF-1248",title:"Add unit tests for retry logic",               status:"TO DO"},
      {id:"ANF-1249",title:"Azure rate limit exceeded — prod collapsing",    status:"IN PROGRESS"},
      {id:"ANF-1250",title:"Update Confluence API documentation",          status:"DONE"},
      {id:"ANF-1251",title:"Refactor auth middleware — PR open",           status:"IN REVIEW"},
      {id:"ANF-1252",title:"Align sprint goals with NetApp PM team",       status:"TO DO"},
    ],
    bhBlockers:[0,2],
    funFact:"He learned JQL (Jira Query Language) from scratch to build the dashboard. Because why not add another language to the list. 🖥️",
    is:{
      teaser:"8 Agile lið. Azure. Google Cloud. Eitt mælaborð til að stjórna þeim öllum.",
      facts:["Yfir verkfræðiforritsstjóri sem samræmir lið yfir Ísland, Kaliforníu og Indland","Áhersla: Azure NetApp Files (ANF) á Microsoft Azure og Google GCP","Kom inn og fann 8 lið sem notuðu Jira á mismunandi hátt — engin sameiginleg sýnileiki"],
      bonusFacts:["Smíðaði sameinað JQL mælaborð: eitt yfirlit yfir öll lið, snemma áhættugreining","Kynnti vikulegar þverlægar fundir yfir alla liðsstjóra","Bein viðmót milli verkfræðinga, NetApp vörustjóra og Microsoft","Hlutverkið flutti til Bandaríkjanna í nóvember 2025. Síðan þá hefur hann notið þess að búa til efni og sjálfvirkniverkefni með gervigreindartækjum."],
      funFact:"Hann lærði JQL (Jira Query Language) frá grunni til að smíða mælaborðið. Vegna þess að hvers vegna ekki bæta við öðru tungumáli á listann. 🖥️",
      bhTickets:[
        {id:"ANF-1247",title:"SDK flutningur — óleyst háðstopp",           status:"BLOCKED"},
        {id:"ANF-1248",title:"Bæta við einingaprófunum fyrir endurtekningarlógík",status:"TO DO"},
        {id:"ANF-1249",title:"Azure hlutfallstakmörk farið yfir — framleiðsla að hrynja",status:"IN PROGRESS"},
        {id:"ANF-1250",title:"Uppfæra Confluence API skjölun",             status:"DONE"},
        {id:"ANF-1251",title:"Endurskipuleggja auth millibúnað — PR opinn",status:"IN REVIEW"},
        {id:"ANF-1252",title:"Samræma sprint markmið með NetApp PM liðinu",status:"TO DO"},
      ],
    },
  },
];

// ── LEVEL LANDSCAPES (SVG backgrounds) ───────────────────────
function LevelScape({ levelId }) {
  const W=1440, H=520;
  const base = { width:"100%", height:"100%", display:"block" };

  // Level 1: Brussels Grand-Place — floodlit Gothic & Baroque facades at night
  if (levelId===1) return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" style={base}>
      <defs>
        <linearGradient id="sky1" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#04020e"/>
          <stop offset="55%" stopColor="#130828"/>
          <stop offset="100%" stopColor="#2a1030"/>
        </linearGradient>
        <pattern id="cobPat" x="0" y="0" width="38" height="18" patternUnits="userSpaceOnUse">
          <rect width="38" height="18" fill="#0e0c08"/>
          <rect x="1" y="1" width="34" height="14" rx="3" fill="#1a160c" stroke="#080604" strokeWidth="0.8"/>
        </pattern>
        <filter id="sglow1"><feGaussianBlur stdDeviation="14"/></filter>
      </defs>
      {/* Night sky */}
      <rect width={W} height={H} fill="url(#sky1)"/>
      {/* Stars */}
      {[70,185,340,475,620,765,930,1090,1245,1390,155,415,650,890,1150].map((x,i)=>(
        <circle key={i} cx={x} cy={10+i%5*16} r={i%4===0?1.8:1} fill="white" opacity={0.3+i%3*0.15}/>
      ))}
      {/* Moon crescent */}
      <circle cx={1330} cy={65} r={26} fill="#f0e098" opacity={0.9}/>
      <circle cx={1318} cy={57} r={22} fill="#130828" opacity={0.88}/>
      {/* Warm ambient glow from floodlit buildings */}
      <ellipse cx={700} cy={355} rx={730} ry={210} fill="#f0a010" opacity={0.2} filter="url(#sglow1)"/>
      {/* Cobblestone square */}
      <rect x={0} y={348} width={W} height={172} fill="url(#cobPat)"/>
      <rect x={250} y={348} width={940} height={172} fill="#f0a010" opacity={0.055}/>

      {/* ── LEFT GUILD HOUSES ── */}
      {/* House 1 — L'Étoile: triangular pediment */}
      <rect x={55} y={262} width={84} height={90} fill="#966e10"/>
      <polygon points="55,262 97,222 139,262" fill="#a47814"/>
      <rect x={93} y={214} width={5} height={14} fill="#744e08"/>
      <circle cx={95} cy={211} r={5} fill="#d4a010"/>
      {[268,293,318].map((y,i)=>[68,100].map(x=>(<rect key={`h1${i}${x}`} x={x} y={y} width={18} height={18} rx={8} fill="#f08810" opacity={0.72}/>)))}
      <path d="M55,352 L55,326 Q76,308 97,326 L97,352" fill="#060402" stroke="#c89010" strokeWidth={1.2}/>
      <path d="M97,352 L97,326 Q118,308 139,326 L139,352" fill="#060402" stroke="#c89010" strokeWidth={1.2}/>

      {/* House 2 — Le Sac: baroque curved gable */}
      <rect x={144} y={238} width={100} height={114} fill="#ae8016"/>
      <path d="M144,238 Q194,198 244,238" fill="#ba8e1a" stroke="#8a600e" strokeWidth={1.5}/>
      <rect x={191} y={191} width={5} height={13} fill="#7a5008"/>
      {[0,1,2,3].map(r=>[155,183,211].map(x=>(<rect key={`h2${r}${x}`} x={x} y={246+r*26} width={18} height={20} rx={9} fill="#f09820" opacity={0.70}/>)))}
      {[152,181,210,237].map(x=>(<rect key={x} x={x} y={240} width={4} height={112} fill="#8a6008" opacity={0.38}/>))}

      {/* House 3 — La Louve: step gable, tallest left */}
      <rect x={249} y={218} width={114} height={134} fill="#be8c1a"/>
      <polygon points="249,218 262,205 274,218" fill="#c49018"/>
      <polygon points="262,205 277,190 289,205" fill="#c49018"/>
      <polygon points="277,190 306,172 335,190" fill="#cc9820"/>
      <polygon points="335,190 347,205 359,218" fill="#c49018"/>
      <polygon points="347,205 356,218 363,218" fill="#be8c1a"/>
      <rect x={302} y={164} width={6} height={14} fill="#7a5008"/>
      {[0,1,2,3].map(r=>[260,290,320,350].map(x=>(<rect key={`h3${r}${x}`} x={x} y={226+r*28} width={18} height={20} rx={9} fill="#ffa030" opacity={0.68}/>)))}
      {[252,280,308,336,357].map(x=>(<rect key={x} x={x} y={220} width={4} height={132} fill="#9a6e0a" opacity={0.35}/>))}

      {/* House 4 — Le Cornet: ship-prow gable */}
      <rect x={368} y={234} width={96} height={118} fill="#aa7e16"/>
      <path d="M368,234 Q392,210 416,200 Q440,210 464,234" fill="#b48a1a" stroke="#8a6010" strokeWidth={1.5}/>
      {[0,1,2,3].map(r=>[376,404,432].map(x=>(<rect key={`h4${r}${x}`} x={x} y={242+r*26} width={18} height={20} rx={9} fill="#f09020" opacity={0.70}/>)))}

      {/* House 5 — L'Arbre d'Or: triangular */}
      <rect x={469} y={250} width={82} height={102} fill="#9a7012"/>
      <polygon points="469,250 510,212 551,250" fill="#a87c14"/>
      <rect x={507} y={204} width={5} height={13} fill="#7a5008"/>
      <circle cx={510} cy={201} r={5} fill="#d4a010"/>
      {[0,1,2,3].map(r=>[478,508].map(x=>(<rect key={`h5${r}${x}`} x={x} y={258+r*24} width={18} height={18} rx={8} fill="#f08810" opacity={0.72}/>)))}

      {/* ── HÔTEL DE VILLE — centerpiece with iconic spire ── */}
      {/* Main building facade */}
      <rect x={556} y={225} width={248} height={128} fill="#c89412"/>
      {[559,592,625,658,691,724,757,790,801].map(x=>(<rect key={x} x={x} y={225} width={5} height={128} fill="#8a6008" opacity={0.42}/>))}
      {[234,266,298].map((y,r)=>[565,597,629,661,693,725,757,779].map((x,c)=>(
        <rect key={`htv${r}${c}`} x={x} y={y} width={20} height={24} rx={9} fill="#ff8818" opacity={0.72}/>
      )))}
      {/* Balustrade cornice */}
      <rect x={554} y={220} width={252} height={8} fill="#d4a018"/>
      {/* Central tower block */}
      <rect x={635} y={125} width={90} height={100} fill="#be8c10"/>
      {[0,1,2].map(r=>[644,672,700].map((x,c)=>(<rect key={`ctl${r}${c}`} x={x} y={133+r*26} width={20} height={20} rx={9} fill="#ff8010" opacity={0.68}/>)))}
      <rect x={630} y={120} width={100} height={8} fill="#c8940e"/>
      {/* Gothic spire base */}
      <rect x={658} y={65} width={44} height={58} fill="#b07e0c"/>
      {/* The spire */}
      <polygon points="642,65 680,12 718,65" fill="#a07208"/>
      {/* Flanking pinnacles */}
      <polygon points="650,70 657,46 664,70" fill="#8a6008"/>
      <polygon points="696,70 703,46 710,70" fill="#8a6008"/>
      {/* Finial + St Michael */}
      <rect x={677} y={7} width={6} height={10} fill="#6a4806"/>
      <circle cx={680} cy={5} r={5} fill="#d4a010"/>
      {[20,36,52].map(y=>(<circle key={y} cx={680} cy={y} r={2} fill="#d4a010" opacity={0.55}/>))}
      {/* Ground floor pointed arcade */}
      {[0,1,2,3,4,5].map(i=>(
        <path key={i} d={`M${558+i*41},353 L${558+i*41},318 Q${579+i*41},296 ${599+i*41},318 L${599+i*41},353`}
          fill="#060402" stroke="#c89010" strokeWidth={1.5}/>
      ))}

      {/* ── MAISON DU ROI (La Broodhuis) ── */}
      <rect x={806} y={220} width={184} height={133} fill="#b88a14"/>
      {[809,843,877,912,947,980].map(x=>(<rect key={x} x={x} y={220} width={5} height={133} fill="#9a6e0a" opacity={0.38}/>))}
      {[229,262,295].map((y,r)=>[814,848,882,917,952,972].map((x,c)=>(
        <rect key={`mdr${r}${c}`} x={x} y={y} width={18} height={24} rx={9} fill="#f09020" opacity={0.68}/>
      )))}
      {/* Neo-Gothic stepped gable */}
      <polygon points="806,220 820,207 832,220" fill="#c09018"/>
      <polygon points="820,207 837,193 849,207" fill="#c09018"/>
      <polygon points="837,193 895,172 953,193" fill="#c89820"/>
      <polygon points="953,193 965,207 977,220" fill="#c09018"/>
      <polygon points="965,207 977,220 990,220" fill="#b88a14"/>
      <rect x={891} y={165} width={6} height={12} fill="#8a6008"/>
      <circle cx={894} cy={161} r={5} fill="#d4a010"/>

      {/* ── RIGHT GUILD HOUSES ── */}
      {/* House A — baroque curved gable */}
      <rect x={996} y={232} width={110} height={122} fill="#ba8a18"/>
      <path d="M996,232 Q1051,192 1106,232" fill="#c29020" stroke="#9a6e0e" strokeWidth={1.5}/>
      <rect x={1048} y={185} width={5} height={13} fill="#7a5008"/>
      {[0,1,2,3].map(r=>[1004,1034,1064].map(x=>(<rect key={`ha${r}${x}`} x={x} y={240+r*26} width={18} height={20} rx={9} fill="#f09830" opacity={0.70}/>)))}

      {/* House B — step gable */}
      <rect x={1112} y={222} width={104} height={132} fill="#b28018"/>
      <polygon points="1112,222 1126,208 1138,222" fill="#ba8e1c"/>
      <polygon points="1126,208 1143,194 1156,208" fill="#ba8e1c"/>
      <polygon points="1143,194 1164,178 1185,194" fill="#c29620"/>
      <polygon points="1185,194 1197,208 1209,222" fill="#ba8e1c"/>
      <polygon points="1197,208 1212,222 1216,222" fill="#b28018"/>
      {[0,1,2,3].map(r=>[1120,1150,1180].map(x=>(<rect key={`hb${r}${x}`} x={x} y={230+r*28} width={18} height={20} rx={9} fill="#f09020" opacity={0.70}/>)))}

      {/* House C — triangular pediment */}
      <rect x={1222} y={248} width={90} height={106} fill="#a27214"/>
      <polygon points="1222,248 1267,206 1312,248" fill="#aa7c18"/>
      <rect x={1264} y={198} width={5} height={13} fill="#7a5008"/>
      <circle cx={1267} cy={195} r={5} fill="#d4a010"/>
      {[0,1,2,3].map(r=>[1230,1260,1286].map(x=>(<rect key={`hc${r}${x}`} x={x} y={256+r*24} width={18} height={18} rx={8} fill="#f08810" opacity={0.72}/>)))}

      {/* Far right building mass */}
      <rect x={1318} y={272} width={122} height={80} fill="#806012"/>
      {[280,306].map((y,i)=>[1326,1358,1388].map(x=>(<rect key={`fd${i}${x}`} x={x} y={y} width={14} height={16} rx={6} fill="#e07810" opacity={0.58}/>)))}

      {/* Far left building mass */}
      <rect x={0} y={280} width={54} height={72} fill="#7a5c10"/>
      {[288,312].map((y,i)=>[8,30].map(x=>(<rect key={`fl${i}${x}`} x={x} y={y} width={14} height={16} rx={6} fill="#e07810" opacity={0.58}/>)))}
    </svg>
  );

  // Level 2: The Hague — Dutch polder, dominant windmill, tulips, canal
  if (levelId===2) return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" style={base}>
      <defs>
        <linearGradient id="sky2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1565a8"/>
          <stop offset="55%" stopColor="#4da2e0"/>
          <stop offset="100%" stopColor="#9ad0f0"/>
        </linearGradient>
        <linearGradient id="canal2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4da2e060"/>
          <stop offset="100%" stopColor="#1a4a7acc"/>
        </linearGradient>
        <linearGradient id="polder2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4a8a2e"/>
          <stop offset="100%" stopColor="#2a5a16"/>
        </linearGradient>
        <linearGradient id="mill2" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#7a4a28"/>
          <stop offset="50%" stopColor="#9a6038"/>
          <stop offset="100%" stopColor="#7a4a28"/>
        </linearGradient>
      </defs>
      {/* Sky */}
      <rect width={W} height={H} fill="url(#sky2)"/>
      {/* Characteristic Dutch puffy cumulus clouds */}
      <ellipse cx={210} cy={78} rx={118} ry={50} fill="white" opacity={0.92}/>
      <ellipse cx={305} cy={64} rx={90} ry={42} fill="white" opacity={0.96}/>
      <ellipse cx={148} cy={92} rx={75} ry={36} fill="white" opacity={0.86}/>
      <ellipse cx={940} cy={90} rx={140} ry={54} fill="white" opacity={0.88}/>
      <ellipse cx={1040} cy={74} rx={100} ry={44} fill="white" opacity={0.92}/>
      <ellipse cx={868} cy={108} rx={85} ry={38} fill="white" opacity={0.82}/>
      <ellipse cx={1270} cy={72} rx={108} ry={46} fill="white" opacity={0.85}/>
      <ellipse cx={1348} cy={58} rx={74} ry={34} fill="white" opacity={0.90}/>
      {/* Flat green Dutch polder */}
      <rect x={0} y={290} width={W} height={230} fill="url(#polder2)"/>
      {/* Step-gabled Dutch houses (right background) */}
      {[1060,1162,1256,1345].map((x,i)=>{
        const h=108-i*10, w=72+i%2*16;
        return (
          <g key={i} opacity={0.88}>
            <rect x={x} y={290-h} width={w} height={h} fill={`hsl(${18+i*14},30%,${22-i*2}%)`}/>
            <polygon points={`${x},${290-h} ${x+w/2},${290-h-26} ${x+w},${290-h}`}
              fill={`hsl(${18+i*14},24%,${17-i*2}%)`}/>
            <rect x={x+9} y={290-h+18} width={16} height={20} rx={2} fill="#f5d07075"/>
            <rect x={x+w-25} y={290-h+18} width={16} height={20} rx={2} fill="#f5d07068"/>
          </g>
        );
      })}
      {/* === WINDMILL (dominant, center-left) === */}
      {/* Body — tapered brick tower */}
      <polygon points="285,302 296,292 312,210 326,166 352,146 388,146 414,166 428,210 444,292 455,302"
        fill="url(#mill2)"/>
      {/* Horizontal brick courses */}
      {[168,190,212,234,256,278].map(y=>{
        const t=(y-146)/(302-146);
        const xL=352-t*(352-285), xR=388+t*(455-388);
        return <line key={y} x1={xL+2} y1={y} x2={xR-2} y2={y} stroke="#5a3010" strokeWidth={1} opacity={0.3}/>;
      })}
      {/* Mill cap (wooden rotating bonnet) */}
      <path d="M348,148 Q370,118 392,148" fill="#5a3820" stroke="#3a2010" strokeWidth={1.5}/>
      <ellipse cx={370} cy={150} rx={46} ry={13} fill="#6a4828"/>
      <line x1={326} y1={150} x2={414} y2={150} stroke="#3a2010" strokeWidth={2}/>
      {/* Hub */}
      <circle cx={370} cy={204} r={12} fill="#3a2010"/>
      <circle cx={370} cy={204} r={6} fill="#8a6030"/>
      {/* Sails at classic 45° parked angle */}
      {[45,135,225,315].map(deg=>{
        const rad=deg*Math.PI/180, L=126, pw=8;
        const tx=370+Math.cos(rad)*L, ty=204+Math.sin(rad)*L;
        const sx=370+Math.cos(rad)*16, sy=204+Math.sin(rad)*16;
        const nx=-Math.sin(rad)*pw, ny=Math.cos(rad)*pw;
        const pts=`${sx+nx},${sy+ny} ${sx-nx},${sy-ny} ${tx-nx*0.2},${ty-ny*0.2} ${tx+nx*0.2},${ty+ny*0.2}`;
        return (
          <g key={deg}>
            <line x1={370} y1={204} x2={tx} y2={ty} stroke="#3a2010" strokeWidth={5}/>
            <polygon points={pts} fill="#c8a860" opacity={0.88}/>
            {[0.38,0.64,0.86].map(t=>{
              const cx2=sx+(tx-sx)*t, cy2=sy+(ty-sy)*t, sc=1-t*0.65;
              return <line key={t} x1={cx2+nx*sc} y1={cy2+ny*sc} x2={cx2-nx*sc} y2={cy2-ny*sc}
                stroke="#4a2a10" strokeWidth={1.8} opacity={0.65}/>;
            })}
          </g>
        );
      })}
      {/* Mill door */}
      <rect x={357} y={266} width={26} height={38} rx={13} fill="#2a1808"/>
      {/* Windows */}
      <rect x={342} y={226} width={16} height={18} rx={7} fill="#1a2a3820"/>
      <rect x={382} y={226} width={16} height={18} rx={7} fill="#1a2a3820"/>
      {/* Mill keeper's cottage at base */}
      <rect x={432} y={262} width={88} height={42} fill="#c09060"/>
      <polygon points="432,262 476,240 520,262" fill="#8a5828"/>
      <rect x={452} y={268} width={14} height={18} rx={1} fill="#2a1808"/>
      <rect x={475} y={270} width={14} height={14} rx={2} fill="#f5d07055"/>
      {/* Distant small windmill (right background) */}
      <g opacity={0.4}>
        <polygon points="920,290 926,288 930,264 934,252 940,247 946,247 952,252 956,264 960,288 966,290"
          fill="#6a4020"/>
        <ellipse cx={943} cy={249} rx={14} ry={5} fill="#4a2e14"/>
        <circle cx={943} cy={262} r={4} fill="#3a2010"/>
        {[0,90,180,270].map(d=>{
          const r2=d*Math.PI/180;
          return <line key={d} x1={943} y1={262} x2={943+Math.cos(r2)*34} y2={262+Math.sin(r2)*34}
            stroke="#3a2010" strokeWidth={2}/>;
        })}
      </g>
      {/* Canal with sky reflection */}
      <path d="M0,382 Q360,372 720,380 Q1080,388 1440,374 L1440,418 L0,418Z" fill="url(#canal2)"/>
      <path d="M30,392 Q200,385 380,393" stroke="white" strokeWidth={1.5} fill="none" opacity={0.26}/>
      <path d="M700,390 Q870,383 1040,390" stroke="white" strokeWidth={1.5} fill="none" opacity={0.20}/>
      {/* Windmill reflection in canal */}
      <g opacity={0.15}>
        <polygon points="330,418 380,418 382,432 383,445 370,453 357,445 358,432 360,418" fill="#9a6038"/>
        <line x1={370} y1={418} x2={370} y2={462} stroke="#3a2010" strokeWidth={2}/>
      </g>
      {/* Tulip rows foreground */}
      {[[0,"#EE1111"],[1,"#FFDD00"],[2,"#FF6600"],[3,"#DD1188"],[4,"#FFDD00"]].map(([row,col])=>(
        <g key={row}>
          {Array.from({length:36},(_,i)=>(
            <g key={i}>
              <rect x={8+i*40+Number(row)*4} y={435-Number(row)*8} width={3} height={20} fill="#2a6a10"/>
              <ellipse cx={9+i*40+Number(row)*4} cy={432-Number(row)*8} rx={8} ry={11}
                fill={col} opacity={0.94}/>
            </g>
          ))}
        </g>
      ))}
    </svg>
  );

  // Level 3: Paris — Champs-Élysées, Arc de Triomphe, Haussmannian architecture
  if (levelId===3) return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" style={base}>
      <defs>
        <linearGradient id="sky3" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1e4a90"/>
          <stop offset="45%" stopColor="#5a90cc"/>
          <stop offset="80%" stopColor="#b8c090"/>
          <stop offset="100%" stopColor="#c8aa70"/>
        </linearGradient>
        <linearGradient id="road3" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8a8478"/>
          <stop offset="100%" stopColor="#5a5448"/>
        </linearGradient>
        <linearGradient id="bldL3" x1="1" y1="0" x2="0" y2="0">
          <stop offset="0%" stopColor="#d4ba90"/>
          <stop offset="100%" stopColor="#c0a870"/>
        </linearGradient>
        <linearGradient id="bldR3" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#d4ba90"/>
          <stop offset="100%" stopColor="#c0a870"/>
        </linearGradient>
        <filter id="far3"><feGaussianBlur stdDeviation="5"/></filter>
        <filter id="mid3"><feGaussianBlur stdDeviation="2"/></filter>
        <mask id="arcMask3">
          <rect x={636} y={143} width={168} height={162} fill="white"/>
          <path d="M694,305 L694,218 Q720,182 746,218 L746,305Z" fill="black"/>
        </mask>
      </defs>
      {/* Sky */}
      <rect width={W} height={H} fill="url(#sky3)"/>
      {/* Soft Parisian clouds */}
      <ellipse cx={280} cy={75} rx={130} ry={38} fill="white" opacity={0.22}/>
      <ellipse cx={385} cy={56} rx={85} ry={27} fill="white" opacity={0.17}/>
      <ellipse cx={1055} cy={90} rx={150} ry={42} fill="white" opacity={0.20}/>
      <ellipse cx={1155} cy={66} rx={95} ry={30} fill="white" opacity={0.15}/>
      {/* Warm golden horizon glow */}
      <rect x={0} y={268} width={W} height={95} fill="#d4aa5020" filter="url(#mid3)"/>
      {/* Far background Haussmannian skyline (hazy) */}
      {[0,130,252,368,476,568,940,1050,1160,1265,1355].map((x,i)=>{
        const h=52+i%3*16;
        return (
          <g key={i} filter="url(#far3)" opacity={0.32}>
            <rect x={x} y={242-h} width={115} height={h} fill="#c4ae80"/>
            <polygon points={`${x},${242-h} ${x+57},${242-h-20} ${x+115},${242-h}`} fill="#7a8a9c"/>
          </g>
        );
      })}
      {/* Eiffel Tower — tiny, far left, hazy */}
      <g opacity={0.28} filter="url(#far3)">
        <polygon points="112,244 120,194 130,178 140,194 148,244" fill="#4a5870"/>
        <polygon points="116,244 122,212 130,205 138,212 144,244" fill="#3a4860"/>
        <polygon points="124,244 127,230 130,226 133,230 136,244" fill="#3a4860"/>
      </g>
      {/* LEFT Haussmannian building */}
      <rect x={0} y={238} width={368} height={282} fill="url(#bldL3)"/>
      <polygon points="0,238 184,204 368,238" fill="#7a8a9c"/>
      {[35,108,182,258].map((x,i)=>(
        <g key={i}>
          <rect x={x} y={212} width={24} height={20} rx={10} fill="#5a6a7c"/>
          <rect x={x+3} y={214} width={18} height={15} rx={8} fill="#8898b040"/>
        </g>
      ))}
      {[55,155,245,310].map(x=>(<rect key={x} x={x} y={200} width={5} height={14} fill="#342a22"/>))}
      <rect x={0} y={234} width={368} height={7} fill="#c0a060"/>
      {[0,1,2].map(floor=>[0,1,2,3,4].map(win=>(
        <g key={`L${floor}-${win}`}>
          <rect x={12+win*70} y={254+floor*44} width={36} height={38} rx={3} fill="#c8e0f838" stroke="#b09050" strokeWidth={1.2}/>
          <line x1={30+win*70} y1={254+floor*44} x2={30+win*70} y2={292+floor*44} stroke="#b09050" strokeWidth={0.7} opacity={0.5}/>
        </g>
      )))}
      {[290,334].map(y=>(<line key={y} x1={8} y1={y} x2={360} y2={y} stroke="#c0a060" strokeWidth={1.8} opacity={0.45}/>))}
      {/* RIGHT Haussmannian building */}
      <rect x={1072} y={238} width={368} height={282} fill="url(#bldR3)"/>
      <polygon points="1072,238 1256,204 1440,238" fill="#7a8a9c"/>
      {[1080,1154,1228,1302].map((x,i)=>(
        <g key={i}>
          <rect x={x} y={212} width={24} height={20} rx={10} fill="#5a6a7c"/>
          <rect x={x+3} y={214} width={18} height={15} rx={8} fill="#8898b040"/>
        </g>
      ))}
      {[1100,1200,1290,1360].map(x=>(<rect key={x} x={x} y={200} width={5} height={14} fill="#342a22"/>))}
      <rect x={1072} y={234} width={368} height={7} fill="#c0a060"/>
      {[0,1,2].map(floor=>[0,1,2,3,4].map(win=>(
        <g key={`R${floor}-${win}`}>
          <rect x={1084+win*70} y={254+floor*44} width={36} height={38} rx={3} fill="#c8e0f838" stroke="#b09050" strokeWidth={1.2}/>
          <line x1={1102+win*70} y1={254+floor*44} x2={1102+win*70} y2={292+floor*44} stroke="#b09050" strokeWidth={0.7} opacity={0.5}/>
        </g>
      )))}
      {[290,334].map(y=>(<line key={y} x1={1080} y1={y} x2={1432} y2={y} stroke="#c0a060" strokeWidth={1.8} opacity={0.45}/>))}
      {/* Arc de Triomphe — masked arch opening reveals sky behind */}
      <rect x={636} y={143} width={168} height={162} fill="#c8b88a" mask="url(#arcMask3)"/>
      <rect x={626} y={136} width={188} height={10} fill="#b8a878"/>
      <rect x={618} y={125} width={204} height={14} fill="#b09870"/>
      <rect x={613} y={114} width={214} height={14} fill="#a89060"/>
      <rect x={644} y={158} width={22} height={120} fill="#b8a870"/>
      <rect x={774} y={158} width={22} height={120} fill="#b8a870"/>
      <line x1={636} y1={174} x2={804} y2={174} stroke="#a89060" strokeWidth={2} opacity={0.4}/>
      <line x1={636} y1={272} x2={804} y2={272} stroke="#a89060" strokeWidth={2} opacity={0.4}/>
      {/* Champs-Élysées avenue (perspective trapezoid) */}
      <polygon points={`${368},${H} ${700},${306} ${740},${306} ${1072},${H}`} fill="url(#road3)"/>
      {/* Sidewalks */}
      <polygon points={`${310},${H} ${672},${306} ${700},${306} ${368},${H}`} fill="#c0b49870"/>
      <polygon points={`${1072},${H} ${740},${306} ${768},${306} ${1130},${H}`} fill="#c0b49870"/>
      {/* Dashed center line (perspective) */}
      {[0.12,0.28,0.44,0.60,0.76,0.92].map((t,i)=>{
        const yA=306+(H-306)*t, yB=306+(H-306)*(t+0.12);
        return <line key={i} x1={720} y1={yA} x2={720} y2={yB} stroke="white" strokeWidth={3*(1.05-t)} opacity={0.18}/>;
      })}
      {/* LEFT chestnut trees in perspective */}
      {[
        {x:362,y:494,r:70},{x:440,y:455,r:57},{x:508,y:423,r:46},
        {x:567,y:397,r:37},{x:617,y:374,r:28},{x:657,y:354,r:21},{x:686,y:338,r:14},
      ].map((t,i)=>(
        <g key={i}>
          <rect x={t.x-3} y={t.y} width={7} height={H-t.y} fill="#4a3520" opacity={0.88}/>
          <circle cx={t.x} cy={t.y-Math.round(t.r*0.38)} r={t.r} fill={`hsl(126,${40-i*3}%,${21+i*2}%)`} opacity={0.93}/>
        </g>
      ))}
      {/* RIGHT chestnut trees in perspective */}
      {[
        {x:1078,y:494,r:70},{x:1000,y:455,r:57},{x:932,y:423,r:46},
        {x:873,y:397,r:37},{x:823,y:374,r:28},{x:783,y:354,r:21},{x:754,y:338,r:14},
      ].map((t,i)=>(
        <g key={i}>
          <rect x={t.x-3} y={t.y} width={7} height={H-t.y} fill="#4a3520" opacity={0.88}/>
          <circle cx={t.x} cy={t.y-Math.round(t.r*0.38)} r={t.r} fill={`hsl(126,${40-i*3}%,${21+i*2}%)`} opacity={0.93}/>
        </g>
      ))}
      {/* Paris-style lamp posts */}
      {[295,1145].map(x=>(
        <g key={x}>
          <rect x={x-2} y={350} width={5} height={170} fill="#252018"/>
          <path d={`M${x+1} 350 Q${x+(x<720?14:-14)} 336 ${x+(x<720?22:-22)} 328`} stroke="#252018" strokeWidth={4} fill="none"/>
          <circle cx={x+(x<720?22:-22)} cy={326} r={7} fill="#FFD16678"/>
          <circle cx={x+(x<720?22:-22)} cy={326} r={15} fill="#FFD16618"/>
        </g>
      ))}
      {/* Foreground pavement */}
      <rect x={0} y={500} width={W} height={20} fill="#b8a88878"/>
    </svg>
  );

  // Level 4: London — misty foggy Thames, Big Ben, Tower Bridge
  if (levelId===4) return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" style={base}>
      <defs>
        <linearGradient id="sky4" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2a3545"/>
          <stop offset="50%" stopColor="#4a5a72"/>
          <stop offset="100%" stopColor="#6a7a8f"/>
        </linearGradient>
        <linearGradient id="thames4" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3a4a60aa"/>
          <stop offset="100%" stopColor="#1a2535cc"/>
        </linearGradient>
        <filter id="fog4"><feGaussianBlur stdDeviation="6"/></filter>
      </defs>
      <rect width={W} height={H} fill="url(#sky4)"/>
      {/* Fog layers */}
      <rect x={0} y={220} width={W} height={80} fill="white" opacity={0.08} filter="url(#fog4)"/>
      <rect x={0} y={280} width={W} height={60} fill="white" opacity={0.06} filter="url(#fog4)"/>
      {/* Big Ben left */}
      <rect x={80} y={100} width={60} height={220} fill="#1a2030"/>
      <rect x={72} y={90} width={76} height={30} fill="#1a2030"/>
      {/* Clock face */}
      <circle cx={110} cy={115} r={22} fill="#2a3545" stroke="#8a9ab0" strokeWidth={2}/>
      <line x1={110} y1={115} x2={110} y2={100} stroke="#c0d0e0" strokeWidth={2}/>
      <line x1={110} y1={115} x2={120} y2={115} stroke="#c0d0e0" strokeWidth={2}/>
      {/* Big Ben spire */}
      <polygon points="72,90 110,40 148,90" fill="#141820"/>
      <rect x={106} y={38} width={8} height={20} fill="#141820"/>
      {/* City buildings left */}
      <rect x={0} y={180} width={80} height={200} fill="#141a26"/>
      <rect x={160} y={200} width={90} height={180} fill="#111825"/>
      <rect x={260} y={220} width={70} height={160} fill="#151c28"/>
      {/* Tower Bridge center-right */}
      {/* Bridge deck */}
      <rect x={600} y={290} width={560} height={18} fill="#1a2535"/>
      {/* Left tower */}
      <rect x={660} y={160} width={55} height={148} fill="#1a2535"/>
      <polygon points="655,160 687,110 720,160" fill="#141e2e"/>
      <rect x={678} y={108} width={18} height={14} fill="#141e2e"/>
      {/* Right tower */}
      <rect x={855} y={160} width={55} height={148} fill="#1a2535"/>
      <polygon points="850,160 882,110 915,160" fill="#141e2e"/>
      <rect x={873} y={108} width={18} height={14} fill="#141e2e"/>
      {/* Bridge cables */}
      <line x1={687} y1={130} x2={600} y2={295} stroke="#2a3a50" strokeWidth={2} opacity={0.7}/>
      <line x1={687} y1={130} x2={760} y2={295} stroke="#2a3a50" strokeWidth={2} opacity={0.7}/>
      <line x1={882} y1={130} x2={760} y2={295} stroke="#2a3a50" strokeWidth={2} opacity={0.7}/>
      <line x1={882} y1={130} x2={1000} y2={295} stroke="#2a3a50" strokeWidth={2} opacity={0.7}/>
      {/* Thames */}
      <path d="M0,300 Q360,285 720,295 Q1080,305 1440,290 L1440,420 L0,420Z" fill="url(#thames4)"/>
      <path d="M100,320 Q400,312 700,318" stroke="white" strokeWidth={2} fill="none" opacity={0.15}/>
      <path d="M800,315 Q1100,308 1400,314" stroke="white" strokeWidth={2} fill="none" opacity={0.12}/>
      {/* Red bus hint */}
      <rect x={340} y={258} width={80} height={36} rx={4} fill="#CC0000" opacity={0.8}/>
      <rect x={345} y={262} width={70} height={16} rx={2} fill="#e60000" opacity={0.6}/>
      <circle cx={360} cy={296} r={8} fill="#222"/>
      <circle cx={405} cy={296} r={8} fill="#222"/>
      {/* Lamp posts */}
      {[200,420,550,900,1100,1280].map((x,i)=>(
        <g key={i}>
          <rect x={x} y={240} width={4} height={62} fill="#3a4a5a"/>
          <circle cx={x+2} cy={238} r={6} fill="#FFD16688" style={{animation:`lava-pulse ${2+i*0.3}s ease-in-out infinite`}}/>
        </g>
      ))}
      {/* Right buildings */}
      <rect x={1150} y={190} width={100} height={200} fill="#141a26"/>
      <rect x={1260} y={210} width={90} height={200} fill="#111825"/>
      <rect x={1360} y={230} width={80} height={200} fill="#151c28"/>
      {/* Foreground */}
      <path d="M0,410 Q360,400 720,408 Q1080,416 1440,404 L1440,520 L0,520Z" fill="#0d1018"/>
    </svg>
  );

  // Level 5: Iceland Northern Lights (original, kept exactly)
  if (levelId===5) return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" style={base}>
      <defs>
        <linearGradient id="sky5" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#020412"/><stop offset="70%" stopColor="#080818"/><stop offset="100%" stopColor="#0a0a20"/>
        </linearGradient>
      </defs>
      <rect width={W} height={H} fill="url(#sky5)"/>
      {[60,140,220,310,390,480,570,650,740,820,910,990,1080,1160,1250,1330,1400,80,200,350,500,680,850,1020,1180,1350].map((x,i)=>(
        <circle key={i} cx={x} cy={20+i%8*18} r={i%3===0?2:1} fill="white" opacity={0.4+i%4*0.15}/>
      ))}
      <path d="M-100,140 Q360,80 720,120 Q1080,160 1540,90" stroke="#06D6A0" strokeWidth="60" fill="none" strokeOpacity="0.2" style={{animation:"aurora 5s ease-in-out infinite"}}/>
      <path d="M-100,170 Q360,110 720,150 Q1080,190 1540,120" stroke="#06D6A0" strokeWidth="35" fill="none" strokeOpacity="0.35" style={{animation:"aurora 5s ease-in-out infinite"}}/>
      <path d="M-100,155 Q360,95 720,135 Q1080,175 1540,105" stroke="#7FFFCF" strokeWidth="15" fill="none" strokeOpacity="0.5" style={{animation:"aurora 5s ease-in-out infinite"}}/>
      <path d="M-100,220 Q300,170 720,200 Q1140,230 1540,170" stroke="#B8A9FF" strokeWidth="45" fill="none" strokeOpacity="0.18" style={{animation:"aurora2 7s ease-in-out infinite 1s"}}/>
      <path d="M-100,240 Q300,190 720,220 Q1140,250 1540,190" stroke="#B8A9FF" strokeWidth="22" fill="none" strokeOpacity="0.28" style={{animation:"aurora2 7s ease-in-out infinite 1s"}}/>
      <path d="M-100,280 Q400,240 720,265 Q1040,290 1540,240" stroke="#74C7EC" strokeWidth="30" fill="none" strokeOpacity="0.15" style={{animation:"aurora 6s ease-in-out infinite 2s"}}/>
      <path d="M0,400 Q100,350 200,400 Q300,450 400,380 Q500,310 600,400 Q700,490 800,400 Q900,310 1000,380 Q1100,450 1200,390 Q1300,330 1440,410 L1440,520 L0,520Z" fill="#050510"/>
      <path d="M0,440 Q200,420 400,445 Q600,470 800,440 Q1000,410 1200,440 Q1380,460 1440,445 L1440,520 L0,520Z" fill="#030308"/>
    </svg>
  );

  // Level 6: Nuremberg — Kaiserburg castle, Fachwerk, Pegnitz, autumn
  if (levelId===6) return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" style={base}>
      <defs>
        <linearGradient id="sky6" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0d1a3a"/>
          <stop offset="45%" stopColor="#1a3a6a"/>
          <stop offset="80%" stopColor="#c07820"/>
          <stop offset="100%" stopColor="#e09030"/>
        </linearGradient>
        <linearGradient id="hill6" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2a1a08"/>
          <stop offset="100%" stopColor="#1a1005"/>
        </linearGradient>
        <linearGradient id="river6" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1a3a6a88"/>
          <stop offset="100%" stopColor="#0d1a3acc"/>
        </linearGradient>
      </defs>
      <rect width={W} height={H} fill="url(#sky6)"/>
      {/* Stars top */}
      {[80,200,350,550,750,950,1150,1300].map((x,i)=>(
        <circle key={i} cx={x} cy={18+i%4*16} r={i%3===0?2:1} fill="white" opacity={0.5+i%3*0.15}/>
      ))}
      {/* Castle hill */}
      <path d="M700,100 Q820,60 940,80 Q1060,100 1150,150 Q1200,180 1220,220 L1440,220 L1440,310 L600,310 Q620,200 700,100Z" fill="url(#hill6)"/>
      <path d="M700,100 Q750,90 800,95 Q850,100 890,110 L900,120 Q860,112 800,108 Q750,105 720,115Z" fill="#3a2a10" opacity={0.5}/>
      {/* Kaiserburg castle on hill */}
      {/* Main keep tower */}
      <rect x={860} y={60} width={55} height={120} fill="#2a1a0a"/>
      <polygon points="855,60 887,20 920,60" fill="#1a1005"/>
      <rect x={878} y={18} width={8} height={18} fill="#1a1005"/>
      {/* Battlements on keep */}
      {[858,868,878,888,898,908].map((x,i)=>(
        <rect key={i} x={x} y={54} width={6} height={10} fill="#1a1005"/>
      ))}
      {/* Round tower left */}
      <rect x={790} y={100} width={45} height={100} fill="#251808"/>
      <ellipse cx={812} cy={100} rx={23} ry={16} fill="#2a1a08"/>
      <polygon points="789,100 812,68 835,100" fill="#1a1005"/>
      {/* Wall connecting */}
      <rect x={835} y={110} width={30} height={70} fill="#201408"/>
      {/* Outer wall */}
      <rect x={700} y={160} width={400} height={60} fill="#251808"/>
      {[705,725,745,765,785,805,825,845,865,885,905,925,945,965,985,1005,1025,1045,1065,1085].map((x,i)=>(
        <rect key={i} x={x} y={152} width={12} height={14} fill="#1a1005"/>
      ))}
      {/* Fachwerk (half-timbered) houses below left */}
      {[80,190,300,420,540].map((x,i)=>{
        const h=100+i%2*20, w=95+i%3*10;
        return (
          <g key={i}>
            <rect x={x} y={280-h} width={w} height={h} fill={`hsl(25,${35-i*3}%,${20+i*2}%)`}/>
            {/* Gable */}
            <polygon points={`${x},${280-h} ${x+w/2},${280-h-30} ${x+w},${280-h}`} fill={`hsl(25,${30-i*3}%,${15+i*2}%)`}/>
            {/* Timber frame cross */}
            <line x1={x} y1={280-h+h*0.33} x2={x+w} y2={280-h+h*0.33} stroke="#0a0602" strokeWidth={3} opacity={0.8}/>
            <line x1={x} y1={280-h+h*0.66} x2={x+w} y2={280-h+h*0.66} stroke="#0a0602" strokeWidth={3} opacity={0.8}/>
            <line x1={x+w*0.33} y1={280-h} x2={x+w*0.33} y2={280} stroke="#0a0602" strokeWidth={2} opacity={0.7}/>
            <line x1={x+w*0.66} y1={280-h} x2={x+w*0.66} y2={280} stroke="#0a0602" strokeWidth={2} opacity={0.7}/>
            {/* Windows */}
            <rect x={x+12} y={280-h+18} width={18} height={20} rx={2} fill="#e09030" opacity={0.65}/>
            <rect x={x+w-30} y={280-h+18} width={18} height={20} rx={2} fill="#e09030" opacity={0.6}/>
          </g>
        );
      })}
      {/* Pegnitz river */}
      <path d="M0,340 Q360,325 720,335 Q1080,345 1440,330 L1440,390 L0,390Z" fill="url(#river6)"/>
      <path d="M80,358 Q300,350 520,356" stroke="white" strokeWidth={2} fill="none" opacity={0.2}/>
      <path d="M800,352 Q1020,345 1240,350" stroke="white" strokeWidth={2} fill="none" opacity={0.18}/>
      {/* Autumn trees */}
      {[60,180,630,1100,1220,1350].map((x,i)=>(
        <g key={i}>
          <rect x={x+16} y={290-i%2*10} width={6} height={60+i%2*15} fill="#3a2010"/>
          <ellipse cx={x+19} cy={285-i%2*10} rx={28} ry={22} fill={["#c07820","#e08030","#d06015","#b87018","#e09020","#c86820"][i]} opacity={0.88}/>
          <ellipse cx={x+8}  cy={292-i%2*8}  rx={18} ry={14} fill={["#d08828","#c07020","#e07020","#d07828","#c86020","#d87830"][i]} opacity={0.75}/>
        </g>
      ))}
      {/* Ground */}
      <path d="M0,380 Q360,370 720,378 Q1080,386 1440,372 L1440,520 L0,520Z" fill="#1a1005"/>
    </svg>
  );

  // Level 7: Alvotech Geyser (original, kept exactly — was level 6)
  if (levelId===7) return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" style={base}>
      <defs>
        <linearGradient id="sky7g" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#e8d080"/><stop offset="40%" stopColor="#c8b060"/><stop offset="100%" stopColor="#a89040"/>
        </linearGradient>
        <radialGradient id="geyser7" cx="50%" cy="100%" r="40%">
          <stop offset="0%" stopColor="#74C7EC" stopOpacity="0.6"/><stop offset="100%" stopColor="#74C7EC" stopOpacity="0"/>
        </radialGradient>
      </defs>
      <rect width={W} height={H} fill="url(#sky7g)"/>
      <path d="M0,380 Q200,365 400,380 Q600,395 800,375 Q1000,355 1200,375 Q1350,390 1440,370 L1440,520 L0,520Z" fill="#2a1a00"/>
      <path d="M0,420 Q300,405 600,422 Q900,440 1200,418 Q1350,407 1440,415 L1440,520 L0,520Z" fill="#1a1000"/>
      <ellipse cx="250" cy="440" rx="80" ry="25" fill="#74C7EC" opacity="0.6"/>
      <ellipse cx="250" cy="435" rx="60" ry="18" fill="#a0dff0" opacity="0.5"/>
      <ellipse cx="1050" cy="450" rx="100" ry="30" fill="#74C7EC" opacity="0.55"/>
      <ellipse cx="1050" cy="444" rx="75" ry="22" fill="#a0dff0" opacity="0.45"/>
      <ellipse cx="1300" cy="430" rx="60" ry="18" fill="#74C7EC" opacity="0.5"/>
      <rect x="700" y="0" width="40" height="380" rx="20" fill="white" opacity="0" style={{animation:"geyser 4s ease-in-out infinite",transformOrigin:"720px 380px"}}/>
      <rect x="690" y="60" width="60" height="320" rx="30" fill="white" opacity="0" style={{animation:"geyser 4s ease-in-out infinite .2s",transformOrigin:"720px 380px"}}/>
      <ellipse cx="720" cy="380" rx="30" ry="10" fill="white" opacity="0.5"/>
      <rect width={W} height={H} fill="url(#geyser7)"/>
      {[250,450,620,850,1050,1200].map((x,i)=>(
        <ellipse key={i} cx={x} cy={380-i*5} rx={6+i%3*3} ry={20+i%4*5} fill="white" opacity="0" style={{animation:`steam ${3+i*0.5}s ease-out infinite ${i*0.8}s`}}/>
      ))}
    </svg>
  );

  // Level 8: The Fjord — deep Nordic fjord, still reflective water
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" style={base}>
      <defs>
        <linearGradient id="sky8" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#060e1a"/>
          <stop offset="40%" stopColor="#0d1e35"/>
          <stop offset="80%" stopColor="#1a3050"/>
          <stop offset="100%" stopColor="#2a4570"/>
        </linearGradient>
        <linearGradient id="water8" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2a4570cc"/>
          <stop offset="100%" stopColor="#060e1aee"/>
        </linearGradient>
        <linearGradient id="cliff8L" x1="1" y1="0" x2="0" y2="0">
          <stop offset="0%" stopColor="#1a2a3a"/>
          <stop offset="100%" stopColor="#0a1520"/>
        </linearGradient>
        <linearGradient id="cliff8R" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#1a2a3a"/>
          <stop offset="100%" stopColor="#0a1520"/>
        </linearGradient>
      </defs>
      <rect width={W} height={H} fill="url(#sky8)"/>
      {/* Stars */}
      {[60,150,250,380,520,680,830,960,1100,1220,1360,100,300,500,700,900,1100,1300].map((x,i)=>(
        <circle key={i} cx={x} cy={12+i%6*20} r={i%4===0?2.5:1} fill="white" opacity={0.3+i%5*0.12}/>
      ))}
      {/* Left fjord cliff wall */}
      <path d="M0,0 L0,520 L480,520 L500,350 Q520,200 490,100 Q465,30 420,0Z" fill="url(#cliff8L)"/>
      <path d="M0,0 L0,520 L360,520 L375,370 Q385,230 362,110 Q338,20 295,0Z" fill="#0a1520"/>
      {/* Snow cap left */}
      <path d="M420,0 Q450,15 490,100 Q480,75 460,50 Q440,28 420,0Z" fill="white" opacity={0.85}/>
      <path d="M295,0 Q320,18 362,110 Q348,78 328,46 Q310,22 295,0Z" fill="white" opacity={0.8}/>
      {/* Left cliff rock detail */}
      <path d="M0,200 Q60,180 120,200 Q160,218 150,260 Q140,290 80,300 Q30,305 0,295Z" fill="#0d1828" opacity={0.6}/>
      <path d="M0,320 Q80,305 140,325 Q170,338 160,370 Q148,395 80,400 Q30,402 0,398Z" fill="#0a1420" opacity={0.5}/>
      {/* Right fjord cliff wall */}
      <path d="M1440,0 L1440,520 L960,520 L940,350 Q920,200 950,100 Q975,30 1020,0Z" fill="url(#cliff8R)"/>
      <path d="M1440,0 L1440,520 L1080,520 L1065,370 Q1055,230 1078,110 Q1102,20 1145,0Z" fill="#0a1520"/>
      {/* Snow cap right */}
      <path d="M1020,0 Q990,15 950,100 Q960,75 980,50 Q1000,28 1020,0Z" fill="white" opacity={0.85}/>
      <path d="M1145,0 Q1120,18 1078,110 Q1092,78 1112,46 Q1130,22 1145,0Z" fill="white" opacity={0.8}/>
      {/* Right cliff rock detail */}
      <path d="M1440,200 Q1380,180 1320,200 Q1280,218 1290,260 Q1300,290 1360,300 Q1410,305 1440,295Z" fill="#0d1828" opacity={0.6}/>
      {/* Still fjord water — mirror reflection */}
      <path d="M460,280 Q720,255 980,280 L980,520 L460,520Z" fill="url(#water8)"/>
      {/* Perfect mirror — cliff reflections in water */}
      <path d="M460,280 Q480,340 500,400 Q510,450 480,520 L460,520Z" fill="#0d1828" opacity={0.4}/>
      <path d="M980,280 Q960,340 940,400 Q930,450 960,520 L980,520Z" fill="#0d1828" opacity={0.4}/>
      {/* Subtle shimmer lines on water */}
      <path d="M500,310 Q720,300 940,310" stroke="white" strokeWidth={1.5} fill="none" opacity={0.18}/>
      <path d="M510,335 Q720,325 930,335" stroke="white" strokeWidth={1} fill="none" opacity={0.13}/>
      <path d="M520,360 Q720,350 920,360" stroke="white" strokeWidth={1} fill="none" opacity={0.1}/>
      <path d="M530,390 Q720,380 910,390" stroke="white" strokeWidth={0.8} fill="none" opacity={0.08}/>
      {/* Very faint sky reflection in water */}
      <ellipse cx={720} cy={400} rx={80} ry={20} fill="#1a3050" opacity={0.25}/>
      {/* Distant background mountain suggestion */}
      <path d="M500,270 Q620,220 720,240 Q820,220 940,270 L940,285 L500,285Z" fill="#0d1828" opacity={0.5}/>
    </svg>
  );
}

// ── FACT PILL ────────────────────────────────────────────────
function FactPill({ text, color }) {
  return (
    <div style={{
      background:"rgba(255,255,255,0.06)", borderLeft:`3px solid ${color}`,
      borderRadius:"0 10px 10px 0", padding:"9px 14px",
      fontFamily:"Nunito,sans-serif", fontSize:14, color:P.white,
      fontWeight:600, lineHeight:1.4,
    }}>{text}</div>
  );
}

// ── FIXED HEADER ─────────────────────────────────────────────
function Header({ completedCount, total, onSkipAll, lang, onLangChange }) {
  const [dancing, setDancing] = useState(false);
  const [skipHovered, setSkipHovered] = useState(false);
  const prevRef = useRef(0);
  const mobile = useIsMobile();
  const t = useT();

  useEffect(()=>{
    if (completedCount > prevRef.current) {
      prevRef.current = completedCount;
      setDancing(true);
      const t = setTimeout(()=>setDancing(false), 1800);
      return ()=>clearTimeout(t);
    }
  }, [completedCount]);

  const pct = total > 0 ? (completedCount / total) * 100 : 0;

  return (
    <div style={{
      position:"fixed", top:0, left:0, right:0, zIndex:1000,
      background:"rgba(13,27,42,0.96)", backdropFilter:"blur(12px)",
      borderBottom:`2px solid ${P.yellow}44`,
      padding:"10px 20px", display:"flex", alignItems:"center", gap:16,
      boxShadow:"0 2px 20px rgba(0,0,0,0.5)",
    }}>
      <div style={{fontFamily:"Bangers,cursive", fontSize:mobile?18:22, color:P.yellow, letterSpacing:2, whiteSpace:"nowrap", textShadow:`2px 2px 0 ${P.black}`}}>
        {mobile ? "🎮" : "🎮 The Gaëtan Quest"}
      </div>
      <div style={{flex:1, position:"relative", height:14,
        background:"rgba(255,255,255,0.08)", borderRadius:7,
        border:"1px solid rgba(255,255,255,0.12)"}}>
        <div style={{
          position:"absolute", left:0, top:0, bottom:0,
          width:`${pct}%`,
          background:`linear-gradient(90deg, ${P.coral}, ${P.peach}, ${P.yellow})`,
          borderRadius:7, transition:"width 1.2s cubic-bezier(.4,0,.2,1)",
          backgroundSize:"200% 100%",
          animation: dancing ? "shimmer 1s linear infinite" : "none",
        }}/>
        {LEVELS.map((_,i)=>(
          <div key={i} style={{
            position:"absolute", left:`${((i+1)/total)*100}%`,
            top:-2, bottom:-2, width:1,
            background:"rgba(255,255,255,0.25)", transform:"translateX(-50%)",
          }}/>
        ))}
        <div style={{
          position:"absolute", top:"50%", left:`${pct}%`,
          transition:"left 1.2s cubic-bezier(.4,0,.2,1)",
          animation: dancing ? "dance 0.45s ease-in-out infinite" : "none",
          zIndex:2, pointerEvents:"none",
        }}>
          <div style={{position:"relative",width:0,height:0}}>
            <div style={{position:"absolute",transform:"translate(-50%,-50%)"}}>
              <img src="/bonhomme.png" alt="Gaëtan"
                style={{width:dancing?44:34, height:dancing?44:34, display:"block", objectFit:"contain"}}/>
            </div>
          </div>
        </div>
      </div>
      <div style={{fontFamily:"Bangers,cursive", fontSize:18, color:P.yellow, whiteSpace:"nowrap"}}>
        {completedCount}<span style={{color:P.muted, fontSize:14}}> / {total}</span>
      </div>
      <div style={{position:"relative", flexShrink:0}}>
        <button
          onClick={onSkipAll}
          onMouseEnter={()=>setSkipHovered(true)}
          onMouseLeave={()=>setSkipHovered(false)}
          style={{
            fontFamily:"Bangers,cursive", fontSize:mobile?12:14, letterSpacing:1,
            color:P.coral,
            background: skipHovered ? "rgba(255,107,107,0.30)" : "rgba(255,107,107,0.12)",
            border:`1px solid ${P.coral}${skipHovered?"cc":"55"}`, borderRadius:8,
            padding:mobile?"4px 7px":"5px 11px", whiteSpace:"nowrap",
            transition:"background .15s, border-color .15s",
          }}
        >
          {mobile?"⏭":t("cutTheCrap")}
        </button>
        {skipHovered && !mobile && (
          <div style={{
            position:"absolute", top:"calc(100% + 8px)", right:0,
            background:"rgba(13,27,42,0.97)", border:`1px solid ${P.coral}55`,
            borderRadius:8, padding:"6px 12px", whiteSpace:"nowrap",
            fontFamily:"Nunito,sans-serif", fontSize:12, fontWeight:700,
            color:P.white, zIndex:2000, boxShadow:"0 4px 16px rgba(0,0,0,0.5)",
            pointerEvents:"none",
          }}>
            {t("cutTheCrapTip")}
          </div>
        )}
      </div>
      {/* Language selector */}
      <div style={{display:"flex", gap:6, flexShrink:0}}>
        {[{code:"en",flag:"🇬🇧"},{code:"is",flag:"🇮🇸"}].map(({code,flag})=>(
          <button key={code} onClick={()=>onLangChange(code)} style={{
            width:30, height:30, borderRadius:"50%", fontSize:18, lineHeight:1,
            border: lang===code ? "2px solid white" : "2px solid rgba(255,255,255,0.25)",
            background: lang===code ? "rgba(255,255,255,0.15)" : "transparent",
            display:"flex", alignItems:"center", justifyContent:"center",
            transition:"border-color .15s, background .15s",
            padding:0,
          }}>{flag}</button>
        ))}
      </div>
    </div>
  );
}

// ── HERO SECTION ─────────────────────────────────────────────
function HeroSection() {
  const bgRef = useRef(null);
  const sectionRef = useRef(null);
  const mobile = useIsMobile();
  const t = useT();
  useEffect(()=>{
    const update = () => {
      if (!sectionRef.current||!bgRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const p = (rect.top + rect.height/2 - window.innerHeight/2) / window.innerHeight;
      bgRef.current.style.transform = `translateY(${p*60}px)`;
    };
    update();
    window.addEventListener("scroll", update, {passive:true});
    return ()=>window.removeEventListener("scroll", update);
  }, []);

  return (
    <section ref={sectionRef} style={{
      position:"relative", height:"100vh", overflow:"hidden",
      display:"flex", alignItems:"center", justifyContent:"center",
    }}>
      <div ref={bgRef} style={{position:"absolute",top:"-10%",left:0,right:0,bottom:"-10%",zIndex:0}}>
        <svg viewBox="0 0 1440 600" preserveAspectRatio="xMidYMid slice" style={{width:"100%",height:"100%"}}>
          <defs>
            <radialGradient id="heroBg" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#1a3a5c"/><stop offset="100%" stopColor="#0D1B2A"/>
            </radialGradient>
          </defs>
          <rect width="1440" height="600" fill="url(#heroBg)"/>
          {[80,180,290,390,500,610,720,830,940,1050,1160,1270,1370].map((x,i)=>(
            <circle key={i} cx={x} cy={30+i%7*25} r={i%4===0?2.5:1.5} fill="white" opacity={0.3+i%5*0.1}/>
          ))}
          <path d="M0,400 Q240,350 480,400 Q720,450 960,380 Q1200,310 1440,390 L1440,600 L0,600Z" fill="#0a1828" opacity="0.9"/>
          <path d="M0,440 Q360,420 720,445 Q1080,470 1440,440 L1440,600 L0,600Z" fill="#060f1a"/>
        </svg>
      </div>
      <div style={{position:"absolute",inset:0,background:"linear-gradient(to bottom,rgba(13,27,42,0.3) 0%,rgba(13,27,42,0.6) 70%,rgba(13,27,42,1) 100%)",zIndex:1}}/>
      <div style={{position:"relative",zIndex:2,textAlign:"center",padding:"0 24px"}}>
        <div style={{display:"inline-flex",flexDirection:mobile?"column":"row",alignItems:"center",gap:mobile?12:20}}>
          <div style={{animation:"float 3s ease-in-out infinite",flexShrink:0}}>
            <img src="/gaetan_souriant.png" alt="Gaëtan Goffe"
              style={{width:mobile?90:140,height:mobile?90:140,borderRadius:"50%",display:"block",objectFit:"cover"}}/>
          </div>
          <Bubble color={P.yellow} pointUp={mobile} pointUpCenter={mobile}>
            {t("heroTagline")}
          </Bubble>
        </div>
        <div style={{fontFamily:"Bangers,cursive",fontSize:"clamp(42px,8vw,80px)",color:P.yellow,
          letterSpacing:4,textShadow:`4px 4px 0 ${P.black}`,marginTop:24,lineHeight:1}}>
          THE GAËTAN QUEST
        </div>
        <div style={{fontFamily:"Nunito,sans-serif",fontSize:16,color:P.sky,fontWeight:700,marginTop:8,letterSpacing:3}}>
          {t("heroSub")}
        </div>
        <div style={{fontFamily:"Nunito,sans-serif",fontSize:14,color:P.muted,marginTop:8}}>
          {t("heroDesc")}
        </div>
        <button onClick={()=>document.getElementById("level-1")?.scrollIntoView({behavior:"smooth"})}
          style={{marginTop:32,animation:"float 2s ease-in-out infinite 0.5s",color:P.muted,fontSize:28,
            background:"none",border:"none",cursor:"pointer",padding:0,lineHeight:1}}>↓</button>
      </div>
    </section>
  );
}

// ── LEVEL SECTION ────────────────────────────────────────────
function LevelSection({ level, isCompleted, onComplete, skipVersion }) {
  const sectionRef = useRef(null);
  const bgRef      = useRef(null);
  const [gameDone,  setGameDone]  = useState(isCompleted);
  const [showBonus, setShowBonus] = useState(isCompleted);
  const [gameCorrect, setGameCorrect] = useState(null);
  const [gameKey, setGameKey] = useState(0);
  const mobile = useIsMobile();

  useEffect(()=>{
    const update = () => {
      if (!sectionRef.current || !bgRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const vh   = window.innerHeight;
      const norm = Math.max(-1, Math.min(1,
        ((rect.top + rect.height / 2) - vh / 2) / vh
      ));
      bgRef.current.style.transform = `translateY(${norm * 55}px)`;
    };
    update();
    window.addEventListener("scroll", update, {passive:true});
    return ()=>window.removeEventListener("scroll", update);
  }, []);

  useEffect(()=>{
    if (skipVersion > 0) {
      setGameDone(true);
      setShowBonus(true);
      setGameCorrect(prev => prev === null ? "skipped" : prev);
    }
  }, [skipVersion]);

  const handleGameDone = useCallback((correct) => {
    setGameCorrect(correct);
    setGameDone(true);
    const shouldShowBonus = correct !== false || level.id !== 1;
    if (shouldShowBonus) {
      setShowBonus(true);
      onComplete(level.id);
    }
  }, [level.id, onComplete]);

  const handleReplay = () => {
    setGameDone(false);
    setGameCorrect(null);
    setShowBonus(false);
    setGameKey(k => k + 1);
  };

  const lang = useLang();
  const lv = (field) => (lang==='is' && level.is?.[field] !== undefined) ? level.is[field] : level[field];
  const t = useT();

  const initialFacts = lv('facts');
  const bonusFacts   = lv('bonusFacts') || [];

  function renderGame() {
    switch (level.gameType) {
      case "quiz":
        return <Quiz q={lv('q')} opts={lv('opts')} correctIdx={level.correct} onDone={handleGameDone} resetOnWrong={level.id===1}/>;
      case "clickorder":
        return <ClickOrder items={lv('coItems')} correct={level.coCorrect} color={level.color} onDone={handleGameDone}/>;
      case "truefalse":
        return <TrueFalse items={lv('tfItems')} color={level.color} onDone={handleGameDone}/>;
      case "fillblank":
        return <FillBlank sentence={lv('fbSentence')} answer={lv('fbAnswer')} hint={lv('fbHint')} color={level.color} onDone={handleGameDone}/>;
      case "memory":
        return <MemoryGame pairs={lv('memPairs')} color={level.color} onDone={handleGameDone}/>;
      case "unscramble":
        return <WordUnscramble scrambled={level.unscrambleScrambled} answer={level.unscrambleWord} hint={lv('unscrambleHint')} color={level.color} onDone={handleGameDone}/>;
      case "multiselect":
        return <MultiSelect question={lv('msQ')} options={level.msOpts} correct={level.msCorrect} color={level.color} onDone={handleGameDone}/>;
      case "matchpairs":
        return <MatchPairs pairs={level.mpPairs} color={level.color} onDone={handleGameDone}/>;
      case "blockerhunt":
        return <BlockerHunt tickets={lv('bhTickets')} blockers={level.bhBlockers} color={level.color} onDone={handleGameDone}/>;
      default:
        return null;
    }
  }

  return (
    <section id={`level-${level.id}`} ref={sectionRef} style={{position:"relative", minHeight:"100vh", overflow:"hidden"}}>

      {/* Background */}
      <div ref={bgRef} style={{position:"absolute",top:"-12%",left:0,right:0,bottom:"-12%",zIndex:0}}>
        <LevelScape levelId={level.id}/>
      </div>
      <div style={{position:"absolute",inset:0,zIndex:1,
        background:`linear-gradient(to bottom, rgba(13,27,42,0.15) 0%, ${level.bg}cc 35%, ${level.bg}f5 55%, ${level.bg} 70%)`
      }}/>

      {/* Completion burst */}
      {showBonus && <CompletionBurst levelId={level.id}/>}

      {/* Content */}
      <div style={{
        position:"relative", zIndex:10,
        maxWidth:760, margin:"0 auto",
        padding:"100px 24px 60px",
        display:"flex", flexDirection:"column", gap:24,
      }}>

        {/* Level badge */}
        <Reveal direction="up" delay={0}>
          <div style={{textAlign:"center"}}>
            <div style={{fontSize:72, animation:"float 3s ease-in-out infinite", display:"inline-block", lineHeight:1}}>
              {level.emoji}
            </div>
            <div style={{fontFamily:"Nunito,sans-serif", color:level.color, fontSize:13, fontWeight:800,
              letterSpacing:4, textTransform:"uppercase", marginTop:8}}>
              Level {level.id} · {level.period}
            </div>
            <h2 style={{fontFamily:"Bangers,cursive", fontSize:"clamp(36px,6vw,60px)",
              color:P.white, letterSpacing:3, marginTop:4, lineHeight:1,
              textShadow:`3px 3px 0 ${P.black}`}}>
              {level.title}
            </h2>
            <div style={{fontFamily:"Nunito,sans-serif", color:P.muted, fontSize:15, fontWeight:600, marginTop:4}}>
              {level.company} · {level.city}
            </div>
          </div>
        </Reveal>

        {/* Gaëtan + teaser */}
        <Reveal direction="left" delay={100}>
          <div style={{display:"flex", flexDirection:mobile?"column":"row", alignItems:mobile?"flex-start":"flex-end", gap:16}}>
            <div style={{flexShrink:0}}>
              <Gaetan mood={gameDone?(gameCorrect===false?"thinking":"happy"):"thinking"} size={80} src={level.portrait||"/gaetan_ref.png"}/>
            </div>
            <Bubble color={level.color} pointUp={mobile}>{lv('teaser')}</Bubble>
          </div>
        </Reveal>

        {/* Initial facts */}
        <Reveal direction="right" delay={200}>
          <div style={{display:"grid", gridTemplateColumns:mobile?"1fr":"1fr 1fr", gap:10}}>
            {initialFacts.map((f,i)=><FactPill key={i} text={f} color={level.color}/>)}
          </div>
        </Reveal>

        {/* Mini-game panel */}
        <Reveal direction="up" delay={300}>
          {!gameDone ? (
            <div style={{
              background:"rgba(0,0,0,0.4)", border:`2px solid ${level.color}55`,
              borderRadius:20, padding:"20px 22px",
              backdropFilter:"blur(8px)",
            }}>
              <div style={{fontFamily:"Bangers,cursive", color:level.color, fontSize:22,
                letterSpacing:2, marginBottom:14, display:"flex", alignItems:"center", gap:8}}>
                {t("quickChallenge")}
              </div>
              <div key={gameKey}>{renderGame()}</div>
            </div>
          ) : gameCorrect === "skipped" ? (
            <div style={{textAlign:"right"}}>
              <button onClick={handleReplay} style={{
                fontFamily:"Nunito,sans-serif", fontWeight:700, fontSize:13,
                color:level.color, background:`${level.color}22`,
                border:`1px solid ${level.color}55`, borderRadius:8,
                padding:"6px 14px",
              }}>{t("playGame")}</button>
            </div>
          ) : (
            <div style={{
              background: gameCorrect ? "rgba(6,214,160,0.12)" : "rgba(255,209,102,0.10)",
              border:`2px solid ${gameCorrect ? P.mint : P.yellow}55`,
              borderRadius:16, padding:"14px 18px",
              fontFamily:"Nunito,sans-serif", fontSize:14, color:P.white, fontWeight:700,
              display:"flex", alignItems:"center", gap:10,
            }}>
              <span style={{fontSize:22}}>{gameCorrect ? "✅" : "💡"}</span>
              <span style={{flex:1}}>
                {gameCorrect
                  ? t("spotOn")
                  : showBonus
                    ? t("goodTryFull")
                    : t("goodTryAgain")}
              </span>
              <button onClick={handleReplay} title="Play again" style={{
                fontFamily:"Nunito,sans-serif", fontWeight:700, fontSize:16,
                color:P.muted, background:"rgba(255,255,255,0.07)",
                border:"1px solid rgba(255,255,255,0.18)", borderRadius:8,
                padding:"4px 10px", flexShrink:0, lineHeight:1,
              }}>🔄</button>
            </div>
          )}
        </Reveal>

        {/* Bonus content */}
        {showBonus && (
          <div style={{animation:"reveal .6s ease both"}}>
            <div style={{
              background:"rgba(0,0,0,0.35)", border:`1px solid ${level.color}33`,
              borderRadius:20, padding:"20px 22px", backdropFilter:"blur(8px)",
            }}>
              <div style={{fontFamily:"Bangers,cursive", color:level.color, fontSize:20,
                letterSpacing:2, marginBottom:14}}>{t("fullPicture")}</div>
              {bonusFacts.length > 0 && (
                <div style={{display:"grid", gridTemplateColumns:mobile?"1fr":"1fr 1fr", gap:10, marginBottom:16}}>
                  {bonusFacts.map((f,i)=><FactPill key={i} text={f} color={level.color}/>)}
                </div>
              )}
              {/* Skills unlocked */}
              {level.skills && level.skills.length > 0 && (
                <div style={{marginBottom:14}}>
                  <div style={{fontFamily:"Bangers,cursive", color:level.color, fontSize:16,
                    letterSpacing:2, marginBottom:8}}>{t("skillsUnlocked")}</div>
                  <div style={{display:"flex", flexWrap:"wrap", gap:6}}>
                    {level.skills.map((s,i)=>(
                      <span key={i} style={{
                        background:`${level.color}22`, border:`1px solid ${level.color}55`,
                        borderRadius:16, padding:"4px 12px",
                        fontFamily:"Nunito", fontSize:12, fontWeight:700, color:P.white,
                      }}>{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {/* Tools */}
              {level.tools && level.tools.length > 0 && (
                <div style={{marginBottom:14}}>
                  <div style={{fontFamily:"Bangers,cursive", color:P.muted, fontSize:16,
                    letterSpacing:2, marginBottom:8}}>{t("toolsTech")}</div>
                  <div style={{display:"flex", flexWrap:"wrap", gap:6}}>
                    {level.tools.map((t,i)=>(
                      <span key={i} style={{
                        background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.18)",
                        borderRadius:16, padding:"4px 12px",
                        fontFamily:"Nunito", fontSize:12, fontWeight:700, color:P.muted,
                      }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}
              <div style={{
                background:`${level.color}18`, border:`1px solid ${level.color}44`,
                borderRadius:12, padding:"12px 16px",
                fontFamily:"Nunito,sans-serif", fontSize:14, color:P.white, lineHeight:1.5,
              }}>
                <span style={{color:level.color, fontWeight:800}}>{t("funFact")} · </span>
                {lv('funFact')}
              </div>
            </div>
            <div style={{textAlign:"center", marginTop:16}}>
              <span style={{
                display:"inline-block",
                background:level.color, color:P.black,
                fontFamily:"Bangers,cursive", fontSize:16, letterSpacing:3,
                padding:"8px 24px", borderRadius:24,
                border:`3px solid ${P.black}`,
                boxShadow:`3px 3px 0 ${P.black}`,
                animation:"badge-pop .5s cubic-bezier(.34,1.56,.64,1) both",
              }}>
                ✓ LEVEL {level.id} COMPLETE
              </span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ── FINAL SECTION ────────────────────────────────────────────
function FinalSection({ completedCount, total }) {
  const allDone = completedCount >= total;
  const sectionRef = useRef(null);
  const bgRef = useRef(null);
  const t = useT();
  useEffect(()=>{
    const update = ()=>{
      if (!sectionRef.current||!bgRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const p = (rect.top + rect.height/2 - window.innerHeight/2) / window.innerHeight;
      bgRef.current.style.transform = `translateY(${p*50}px)`;
    };
    update();
    window.addEventListener("scroll", update, {passive:true});
    return ()=>window.removeEventListener("scroll", update);
  }, []);

  return (
    <section ref={sectionRef} style={{position:"relative", minHeight:"100vh", overflow:"hidden",
      display:"flex", flexDirection:"column", alignItems:"stretch", justifyContent:"center"}}>
      <div ref={bgRef} style={{position:"absolute",top:"-10%",left:0,right:0,bottom:"-10%",zIndex:0}}>
        <svg viewBox="0 0 1440 600" preserveAspectRatio="xMidYMid slice" style={{width:"100%",height:"100%"}}>
          <defs>
            <radialGradient id="finalBg" cx="50%" cy="50%" r="70%">
              <stop offset="0%" stopColor="#2a1a00"/><stop offset="100%" stopColor="#0D1B2A"/>
            </radialGradient>
          </defs>
          <rect width="1440" height="600" fill="url(#finalBg)"/>
          {[100,240,380,520,660,800,940,1080,1220,1360].map((x,i)=>(
            <circle key={i} cx={x} cy={40+i%6*30} r={i%3===0?3:1.5} fill={i%2===0?P.yellow:P.sky} opacity={0.4+i%4*0.1}/>
          ))}
        </svg>
      </div>
      <div style={{position:"absolute",inset:0,background:"linear-gradient(to bottom,rgba(13,27,42,0.2),rgba(13,27,42,0.85))",zIndex:1}}/>
      <div style={{position:"relative",zIndex:2,flex:1,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{maxWidth:680,width:"100%",margin:"0 auto",padding:"80px 24px",textAlign:"center"}}>
        {allDone ? (
          <>
            <div style={{display:"flex",justifyContent:"center",marginBottom:20}}>
              <img src="/party.png" alt="Quest complete!" style={{
                width:200, height:200, borderRadius:"50%", display:"block", objectFit:"cover",
                animation:"float 3s ease-in-out infinite",
                boxShadow:`0 0 50px ${P.yellow}66, 0 0 100px ${P.yellow}33`,
                border:`4px solid ${P.yellow}`,
              }}/>
            </div>
            <div style={{fontFamily:"Bangers,cursive",fontSize:52,color:P.yellow,letterSpacing:3,marginBottom:8,textShadow:`4px 4px 0 ${P.black}`}}>
              {t("questComplete")}
            </div>
            <p style={{fontFamily:"Nunito,sans-serif",fontSize:15,color:P.white,lineHeight:1.75,marginBottom:24}}>
              {t("questDesc")}
            </p>
          </>
        ) : (
          <>
            <div style={{fontSize:64,marginBottom:16}}>🔒</div>
            <div style={{fontFamily:"Bangers,cursive",fontSize:40,color:P.muted,letterSpacing:2,marginBottom:12}}>
              {t("completeAll")}
            </div>
            <p style={{fontFamily:"Nunito,sans-serif",fontSize:15,color:P.muted,lineHeight:1.7,marginBottom:20}}>
              {t("completeDesc",completedCount,total)}
            </p>
          </>
        )}
        <div style={{display:"flex",flexWrap:"wrap",justifyContent:"center",gap:10,marginBottom:28}}>
          {["Prince2 Practitioner","ITIL v3","Agile PM","PMP","Java/COBOL/SQL","React","Jira/JQL","HTML/CSS/JS","Kotlin","Firebase","GMP","Azure/GCP","AI"].map(s=>(
            <span key={s} style={{
              background: allDone ? "rgba(255,209,102,0.15)" : "rgba(255,255,255,0.06)",
              border:`1px solid ${allDone ? P.yellow+"55" : "rgba(255,255,255,0.12)"}`,
              borderRadius:20, padding:"5px 14px",
              fontFamily:"Nunito,sans-serif", fontSize:12, fontWeight:700,
              color: allDone ? P.yellow : P.muted,
              transition:"all .4s",
            }}>{s}</span>
          ))}
        </div>
        <a href="https://www.linkedin.com/in/ga%C3%ABtan-goffe-5b333839/" target="_blank" rel="noopener noreferrer"
          style={{
            display:"inline-block",
            background: allDone ? P.yellow : "rgba(255,255,255,0.1)",
            color: allDone ? P.black : P.muted,
            border:`3px solid ${allDone ? P.black : "rgba(255,255,255,0.2)"}`,
            borderRadius:14, padding:"12px 32px",
            fontFamily:"Bangers,cursive", fontSize:22, letterSpacing:2,
            textDecoration:"none",
            boxShadow: allDone ? `4px 4px 0 ${P.black}` : "none",
            transition:"all .4s",
          }}>
          {allDone ? t("connectLinkedin") : t("unlockConnect")}
        </a>
      </div>
      </div>
      <div style={{
        position:"relative", zIndex:2,
        textAlign:"center", padding:"16px 24px",
        color:"rgba(255,255,255,0.5)", fontSize:11, letterSpacing:0.5,
        borderTop:"1px solid rgba(255,255,255,0.12)",
      }}>
        {t("footer")}
      </div>
    </section>
  );
}

// ── APP ──────────────────────────────────────────────────────
export default function App() {
  const [completed, setCompleted] = useState(new Set());
  const [skipVersion, setSkipVersion] = useState(0);
  const [lang, setLang] = useState('en');

  const handleComplete = useCallback((id) => {
    setCompleted(prev => {
      if (prev.has(id)) return prev;
      return new Set([...prev, id]);
    });
  }, []);

  const completeAll = useCallback(() => {
    setCompleted(new Set(LEVELS.map(l => l.id)));
    setSkipVersion(v => v + 1);
  }, []);

  return (
    <LangCtx.Provider value={lang}>
      <style>{FONTS}</style>
      <style>{CSS}</style>
      <Header completedCount={completed.size} total={LEVELS.length} onSkipAll={completeAll} lang={lang} onLangChange={setLang}/>
      <HeroSection/>
      {LEVELS.map(level => (
        <LevelSection
          key={level.id}
          level={level}
          isCompleted={completed.has(level.id)}
          onComplete={handleComplete}
          skipVersion={skipVersion}
        />
      ))}
      <FinalSection completedCount={completed.size} total={LEVELS.length}/>
    </LangCtx.Provider>
  );
}
